import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, Maximize2, Settings, Play, Edit3, PhoneCall, BookOpen, Search, Upload, FileText, CheckCircle, Clock, XCircle, Trash2, Sparkles, AlertCircle, Phone, PhoneOff, Code, Zap, Plus, ToggleLeft, ToggleRight, Copy, Clipboard, Bot, Mic, Mic2, Shield, Webhook, PhoneForwarded, FlaskConical, Database, MessageSquare, Music, UserCircle, GitBranch, Image, Variable, Voicemail, Hash, Timer } from 'lucide-react';
import { customFunctions, knowledgeBase, agents } from '../api';
import Toast from './Toast';
import TestAgentInterfaceInline from './TestAgentInterfaceInline';
import ChatTestInterface from './ChatTestInterface';
import ChatEmbedWidget from './ChatEmbedWidget';
import BackgroundNoisesTab from './BackgroundNoisesTab';
import RemindersTab from './RemindersTab';
import CallLimitsTab from './CallLimitsTab';
import AgentEndCallTab from './AgentEndCallTab';
import VoicemailDropTab from './VoicemailDropTab';
import TransferRulesEditor from './TransferRulesEditor';
import DtmfTab from './DtmfTab';
import NoiseCancellationTab from './NoiseCancellationTab';
import AiAcousticsTab from './AiAcousticsTab';
import AnimatedDropdown from './AnimatedDropdown';
import AnimatedCheckbox from './AnimatedCheckbox';
import MultiSelect from './MultiSelect';
import LoadingSpinner from './LoadingSpinner';
import FlowBuilder from './FlowBuilder';
import ProductImagesTab from './ProductImagesTab';
import DefaultVariablesTab from './DefaultVariablesTab';
import AvatarPanelThemeCustomizer from './AvatarPanelThemeCustomizer';
import api from '../api';

// Language options for Gladia and Soniox STT (Soniox supports 60+ languages)
const GLADIA_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ht', label: 'Haitian Creole' },
  { value: 'ru', label: 'Russian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pl', label: 'Polish' },
  { value: 'tr', label: 'Turkish' },
  { value: 'sv', label: 'Swedish' },
  { value: 'da', label: 'Danish' },
  { value: 'no', label: 'Norwegian' },
  { value: 'fi', label: 'Finnish' },
  { value: 'cs', label: 'Czech' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'ro', label: 'Romanian' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'el', label: 'Greek' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'th', label: 'Thai' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ms', label: 'Malay' },
  { value: 'he', label: 'Hebrew' },
  { value: 'ca', label: 'Catalan' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'hr', label: 'Croatian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'et', label: 'Estonian' },
  { value: 'lv', label: 'Latvian' },
  { value: 'lt', label: 'Lithuanian' },
];

// Soniox stt-rt-v3 supports 60 languages. These are the ones verified against
// GET https://api.soniox.com/v1/models plus a live transcription and translation
// check, and are offered on top of the shared list for the Soniox language hints
// and translation dropdowns.
const SONIOX_EXTRA_LANGUAGE_OPTIONS = [
  { value: 'tl', label: 'Tagalog' },
  { value: 'ur', label: 'Urdu' },
];

const SONIOX_LANGUAGE_OPTIONS = [
  ...GLADIA_LANGUAGE_OPTIONS,
  ...SONIOX_EXTRA_LANGUAGE_OPTIONS,
];

// Cartesia TTS languages, probed against the live API on 2026-09-21. Sonic 3.6
// speaks all 44; Sonic 3 speaks the same list minus Urdu and Odia; the legacy
// sonic-2 model behind the plain 'cartesia' provider speaks only 8. Sending a
// code the model does not support is a hard 400, so the picker only ever
// offers what the selected model can actually speak.
const CARTESIA_LANGUAGE_OPTIONS = [
  { value: 'ar', label: 'Arabic' },
  { value: 'bn', label: 'Bengali' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'zh', label: 'Chinese' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'en', label: 'English' },
  { value: 'fi', label: 'Finnish' },
  { value: 'fr', label: 'French' },
  { value: 'ka', label: 'Georgian' },
  { value: 'de', label: 'German' },
  { value: 'el', label: 'Greek' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hi', label: 'Hindi' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'kn', label: 'Kannada' },
  { value: 'ko', label: 'Korean' },
  { value: 'ms', label: 'Malay' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'mr', label: 'Marathi' },
  { value: 'no', label: 'Norwegian' },
  { value: 'or', label: 'Odia' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ro', label: 'Romanian' },
  { value: 'ru', label: 'Russian' },
  { value: 'sk', label: 'Slovak' },
  { value: 'es', label: 'Spanish' },
  { value: 'sv', label: 'Swedish' },
  { value: 'tl', label: 'Tagalog' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'th', label: 'Thai' },
  { value: 'tr', label: 'Turkish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'vi', label: 'Vietnamese' },
];

const SONIC_3_UNSUPPORTED_LANGUAGES = ['or', 'ur'];
const SONIC_2_LANGUAGES = ['de', 'en', 'es', 'fr', 'ja', 'ko', 'pt', 'zh'];

const AgentFormModal = ({ editingAgent, formData, setFormData, onSubmit, onCancel }) => {
  const [activeTab, setActiveTab] = useState('basic');
  // Test / Chat Test only: sessions started while this is on fire no webhooks.
  // Not part of the agent config, so it never goes into formData.
  const [suppressTestWebhooks, setSuppressTestWebhooks] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [isChatPromptExpanded, setIsChatPromptExpanded] = useState(false);
  const [isGlobalPromptExpanded, setIsGlobalPromptExpanded] = useState(false);
  const [functions, setFunctions] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [loadingFunctions, setLoadingFunctions] = useState(false);
  const [testingFunction, setTestingFunction] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [editingFunctionData, setEditingFunctionData] = useState(null);
  const [savingFunction, setSavingFunction] = useState(false);
  const [toast, setToast] = useState(null);
  const [showAvatarPanelCustomizer, setShowAvatarPanelCustomizer] = useState(false);
  const [creatingFunction, setCreatingFunction] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [pasteError, setPasteError] = useState('');
  const [deleteFunctionModal, setDeleteFunctionModal] = useState({ open: false, functionId: null, functionName: '' });
  const [newFunctionData, setNewFunctionData] = useState({
    name: '',
    description: '',
    trigger_event: 'call_started',
    target_agent_id: '', // Will be set to editingAgent.id when creating
    webhook_url: '',
    http_method: 'POST',
    parameters: '{}',
    system_prompt_modification: '',
    greeting_modification: '',
    is_active: true
  });
  // Knowledge Base state
  const [kbDocuments, setKbDocuments] = useState([]);
  const [loadingKbDocuments, setLoadingKbDocuments] = useState(false);
  const [kbQueryText, setKbQueryText] = useState('');
  const [kbQueryResults, setKbQueryResults] = useState([]);
  const [kbQuerying, setKbQuerying] = useState(false);
  const [kbUploading, setKbUploading] = useState(false);
  const [kbSelectedFile, setKbSelectedFile] = useState(null);
  const [kbUploadTitle, setKbUploadTitle] = useState('');
  const [kbDeleteModal, setKbDeleteModal] = useState({ show: false, docId: null });
  // Outbound Test state
  const [outboundPhoneNumbers, setOutboundPhoneNumbers] = useState([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [outboundTestCall, setOutboundTestCall] = useState({
    from_number: '',
    to_number: '',
    agent_id: null,
    variables: {},
    overrides: {}
  });
  const [outboundVariablesJson, setOutboundVariablesJson] = useState('{}');
  const [outboundOverridesJson, setOutboundOverridesJson] = useState('{}');
  const [outboundCalling, setOutboundCalling] = useState(false);
  const [outboundVars, setOutboundVars] = useState([{ key: '', value: '' }]);
  const [outboundOverrideVars, setOutboundOverrideVars] = useState([{ key: '', value: '' }]);
  const [showOutboundVarsJson, setShowOutboundVarsJson] = useState(false);
  const [showOutboundOverridesJson, setShowOutboundOverridesJson] = useState(false);
  // Data Extraction state
  const [extractionFields, setExtractionFields] = useState([]);
  const [loadingExtractionFields, setLoadingExtractionFields] = useState(false);
  const [editingExtractionField, setEditingExtractionField] = useState(null);
  const [newExtractionField, setNewExtractionField] = useState({
    name: '',
    description: '',
    data_type: 'text',
    skip_if_not_found: false,
    format_example: '',
    choices: [], // Array of choice strings
    display_order: 0,
    is_active: true
  });
  const [showExtractionFieldForm, setShowExtractionFieldForm] = useState(false);
  const [extractionFieldDeleteModal, setExtractionFieldDeleteModal] = useState({ show: false, fieldId: null, fieldName: '' });
  // PII Types state
  const [customPiiTypes, setCustomPiiTypes] = useState({});
  const [disabledDefaultTypes, setDisabledDefaultTypes] = useState([]);
  const [newPiiType, setNewPiiType] = useState({ name: '', placeholder: '' });
  const [piiPreviewText, setPiiPreviewText] = useState('');
  const [piiPreviewResult, setPiiPreviewResult] = useState(null);
  const [piiPreviewLoading, setPiiPreviewLoading] = useState(false);
  const [backchannelingWordsInput, setBackchannelingWordsInput] = useState('');
  // Agent Transfer state
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const flowBuilderRef = useRef(null);

  const isCartesiaSonicVoice = useMemo(() => {
    if (formData.voice_provider !== 'cartesia') return false;
    const voiceId = (formData.cartesia_voice_id || '').trim().toLowerCase();
    return voiceId.startsWith('sonic-') || voiceId.startsWith('sonic_');
  }, [formData.voice_provider, formData.cartesia_voice_id]);

  const supportsTemperatureStability = useMemo(() => {
    if (formData.voice_provider === 'elevenlabs') return true;
    if (formData.voice_provider === 'cartesia') return isCartesiaSonicVoice;
    if (formData.voice_provider === 'sonic-3') return false;
    return false;
  }, [formData.voice_provider, isCartesiaSonicVoice]);

  const supportsVoiceVolume = useMemo(() => {
    if (formData.voice_provider === 'elevenlabs') return true;
    return false;
  }, [formData.voice_provider]);

  const supportsElevenLabsSpeed = useMemo(() => {
    if (formData.voice_provider === 'elevenlabs') return true;
    return false;
  }, [formData.voice_provider]);

  const voiceControlCount =
    (supportsTemperatureStability ? 2 : 0) +
    (supportsVoiceVolume ? 1 : 0) +
    (supportsElevenLabsSpeed ? 1 : 0);

  const voiceControlsGridClass =
    voiceControlCount >= 3
      ? 'md:grid-cols-3'
      : voiceControlCount === 2
      ? 'md:grid-cols-2'
      : 'md:grid-cols-1';
  const showNoControlMessage =
    voiceControlCount === 0 &&
    formData.voice_provider !== 'sonic-3' &&
    formData.voice_provider !== 'fishaudio';

  // Sonic 3.6 sets its own pacing and reads emotion from the script, so the
  // speed and emotion controls below do not move it. Volume still applies.
  // Verified against the Cartesia API: on sonic-3 a 0.6x/1.5x speed swing
  // changes audio duration ~2.2x, on sonic-3.6 it changes it ~1.15x.
  const isSonic36 = useMemo(
    () =>
      formData.voice_provider === 'sonic-3' &&
      (formData.sonic_model || 'sonic-3') === 'sonic-3.6',
    [formData.voice_provider, formData.sonic_model]
  );

  // The plain 'cartesia' provider runs on sonic-2, which speaks only 8 of the
  // languages; sonic-3 speaks all but Urdu and Odia; sonic-3.6 speaks all 44.
  const cartesiaLanguageOptions = useMemo(() => {
    let allowed = null;
    if (formData.voice_provider === 'cartesia') {
      allowed = SONIC_2_LANGUAGES;
    } else if (formData.voice_provider === 'sonic-3' && (formData.sonic_model || 'sonic-3') === 'sonic-3') {
      allowed = CARTESIA_LANGUAGE_OPTIONS
        .map((o) => o.value)
        .filter((v) => !SONIC_3_UNSUPPORTED_LANGUAGES.includes(v));
    }
    const list = allowed
      ? CARTESIA_LANGUAGE_OPTIONS.filter((o) => allowed.includes(o.value))
      : CARTESIA_LANGUAGE_OPTIONS;
    return [{ value: '', label: 'Follow agent Language setting' }, ...list];
  }, [formData.voice_provider, formData.sonic_model]);

  const sonicEmotionOptions = useMemo(
    () => [
      { value: 'neutral', label: 'Neutral (default)' },
      { value: 'excited', label: 'Excited' },
      { value: 'calm', label: 'Calm' },
      { value: 'content', label: 'Content' },
      { value: 'sad', label: 'Sad' },
      { value: 'angry', label: 'Angry' },
      { value: 'enthusiastic', label: 'Enthusiastic' },
      { value: 'determined', label: 'Determined' },
      { value: 'mysterious', label: 'Mysterious' },
      { value: 'happy', label: 'Happy' },
      { value: 'scared', label: 'Scared' },
    ],
    []
  );

  const fishaudioModelOptions = useMemo(
    () => [
      { value: 's2.1-pro', label: 'S2.1 Pro (recommended, 83 languages)' },
      { value: 's2.1-pro-free', label: 'S2.1 Pro Free (dev tier, fair-use limits)' },
      { value: 's2-pro', label: 'S2 Pro (previous generation)' },
      { value: 's1', label: 'S1 (legacy, 13 languages)' },
    ],
    []
  );

  const fishaudioLatencyOptions = useMemo(
    () => [
      { value: 'balanced', label: 'Balanced (default)' },
      { value: 'low', label: 'Low (fastest, lower quality)' },
      { value: 'normal', label: 'Normal (best quality)' },
    ],
    []
  );

  const fishaudioPaceOptions = useMemo(
    () => [
      { value: 'normal', label: 'Normal' },
      { value: 'slow', label: 'Slow' },
      { value: 'fast', label: 'Fast' },
    ],
    []
  );

  // Expressive mode is driven by [bracket] delivery directions, which only the
  // S2-Pro family understands. S1 reads a different (parenthesis) dialect.
  const fishaudioSupportsExpressive = useMemo(
    () => ['s2-pro', 's2.1-pro', 's2.1-pro-free'].includes(formData.fishaudio_model || 's2.1-pro'),
    [formData.fishaudio_model]
  );

  const OB_VARS_KEY = editingAgent ? `braincx-outbound-vars-${editingAgent.id}` : null;
  const OB_OVERRIDES_KEY = editingAgent ? `braincx-outbound-overrides-${editingAgent.id}` : null;

  const kvToObject = useCallback((arr) => {
    const obj = {};
    for (const v of arr) { const k = v.key.trim(); if (k) obj[k] = v.value; }
    return obj;
  }, []);

  const loadKvFromStorage = useCallback((storageKey) => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) { const p = JSON.parse(raw); if (Array.isArray(p) && p.length > 0) return p; }
    } catch (_) {}
    return [{ key: '', value: '' }];
  }, []);

  const saveKvToStorage = useCallback((storageKey, arr) => {
    try { localStorage.setItem(storageKey, JSON.stringify(arr)); } catch (_) {}
  }, []);

  const updateOutboundVars = useCallback((updater) => {
    setOutboundVars(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (OB_VARS_KEY) saveKvToStorage(OB_VARS_KEY, next);
      setOutboundVariablesJson(JSON.stringify(kvToObject(next), null, 2));
      setOutboundTestCall(c => ({ ...c, variables: kvToObject(next) }));
      return next;
    });
  }, [OB_VARS_KEY, saveKvToStorage, kvToObject]);

  const updateOutboundOverrideVars = useCallback((updater) => {
    setOutboundOverrideVars(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (OB_OVERRIDES_KEY) saveKvToStorage(OB_OVERRIDES_KEY, next);
      setOutboundOverridesJson(JSON.stringify(kvToObject(next), null, 2));
      setOutboundTestCall(c => ({ ...c, overrides: kvToObject(next) }));
      return next;
    });
  }, [OB_OVERRIDES_KEY, saveKvToStorage, kvToObject]);

  useEffect(() => {
    if (editingAgent) {
      setOutboundVars(loadKvFromStorage(`braincx-outbound-vars-${editingAgent.id}`));
      setOutboundOverrideVars(loadKvFromStorage(`braincx-outbound-overrides-${editingAgent.id}`));
    }
  }, [editingAgent?.id, loadKvFromStorage]);

  useEffect(() => {
    if (editingAgent && activeTab === 'functions') {
      loadAgentFunctions();
    } else if (activeTab !== 'functions') {
      // Reset selected function when switching away from functions tab
      setSelectedFunction(null);
    }
    if (editingAgent && activeTab === 'knowledge-base') {
      loadAgentDocuments();
    }
    if (editingAgent && activeTab === 'outbound-test') {
      loadAgentPhoneNumbers();
    }
    if (editingAgent && activeTab === 'data-extraction') {
      loadExtractionFields();
    }
    if (editingAgent && activeTab === 'pii') {
      // Load PII types from formData
      // New format: final list of active types {type: placeholder}
      // Old format: {_disabled_defaults: [...], custom_type: placeholder}
      try {
        const piiTypesStr = formData.custom_pii_types;
        if (piiTypesStr && piiTypesStr.trim()) {
          const piiData = JSON.parse(piiTypesStr);
          
          // Check if old format with _disabled_defaults
          if ('_disabled_defaults' in piiData) {
            // Old format - extract disabled and custom
            const disabled = piiData._disabled_defaults || [];
            const custom = { ...piiData };
            delete custom._disabled_defaults;
            setDisabledDefaultTypes(disabled);
            setCustomPiiTypes(custom);
          } else {
            // New format - reconstruct disabled defaults by comparing with all defaults
            const defaultTypes = ['name', 'email', 'phone', 'ssn', 'credit_card', 'address', 'date_of_birth', 'driver_license', 'passport', 'bank_account', 'ip_address', 'medical_record', 'insurance_id'];
            const activeTypes = Object.keys(piiData);
            const disabled = defaultTypes.filter(t => !activeTypes.includes(t));
            const custom = {};
            // Custom types are those not in default list
            for (const [type, placeholder] of Object.entries(piiData)) {
              if (!defaultTypes.includes(type)) {
                custom[type] = placeholder;
              }
            }
            setDisabledDefaultTypes(disabled);
            setCustomPiiTypes(custom);
          }
        } else {
          setDisabledDefaultTypes([]);
          setCustomPiiTypes({});
        }
      } catch (e) {
        console.error('Failed to parse custom_pii_types:', e);
        setDisabledDefaultTypes([]);
        setCustomPiiTypes({});
      }
    }
    if (activeTab === 'backchanneling') {
      // Load backchanneling words from formData
      // Reset input field when switching to this tab or when formData changes
      const wordsValue = formData.backchanneling_words;
      
      // Always reset to empty string first, then populate if there's a valid value
      if (!wordsValue || (typeof wordsValue === 'string' && wordsValue.trim() === '')) {
        setBackchannelingWordsInput('');
      } else {
        try {
          const words = JSON.parse(wordsValue);
          if (Array.isArray(words) && words.length > 0) {
            setBackchannelingWordsInput(words.join(', '));
          } else {
            setBackchannelingWordsInput('');
          }
        } catch (e) {
          // If parsing fails, clear the input
          setBackchannelingWordsInput('');
        }
      }
    }
    if (activeTab === 'agent-transfer') {
      loadAvailableAgents();
    }
  }, [editingAgent, activeTab, formData.backchanneling_words]);

  // Listen for flow updates from maximized flow editor window
  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'flow-editor-apply') {
        // Make the inline canvas rebuild from the applied flow. Otherwise it keeps
        // the old nodes and Save (which reads getCurrentFlow) overwrites this flow.
        if (event.data.flowData) {
          flowBuilderRef.current?.reloadFromFlowData?.();
        }
        setFormData((prev) => ({
          ...prev,
          conversation_flow: event.data.flowData ?? prev.conversation_flow,
          required_variables: event.data.requiredVariables ?? prev.required_variables,
        }));
        setToast({ message: 'Flow updated from maximized editor', type: 'success' });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [setFormData, setToast]);

  const loadAvailableAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await agents.list();
      // Filter out the current agent and archived agents
      const filtered = (response.data || []).filter(
        (agent) => agent.id !== editingAgent?.id && !agent.is_archived
      );
      setAvailableAgents(filtered);
    } catch (error) {
      console.error('Failed to load agents:', error);
      setAvailableAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadAgentFunctions = async () => {
    if (!editingAgent) return;
    
    setLoadingFunctions(true);
    try {
      const response = await customFunctions.list();
      const allFunctions = response.data.functions || [];
      // Filter functions mapped to this agent or all agents
      const agentFunctions = allFunctions.filter(
        (func) => !func.target_agent_id || func.target_agent_id === editingAgent.id
      );
      setFunctions(agentFunctions);
      
      // Only auto-select first function if no function is currently selected
      // or if the currently selected function is no longer in the list
      if (agentFunctions.length > 0) {
        if (!selectedFunction || !agentFunctions.find(f => f.id === selectedFunction.id)) {
          setSelectedFunction(agentFunctions[0]);
        }
      } else {
        setSelectedFunction(null);
      }
    } catch (error) {
      console.error('Failed to load functions:', error);
    } finally {
      setLoadingFunctions(false);
    }
  };

  const handleTestFunction = async (func) => {
    setTestingFunction(true);
    setTestResult(null);
    setTestError(null);
    
    try {
      const testPayload = typeof func.parameters_schema === 'string' 
        ? JSON.parse(func.parameters_schema || '{}')
        : func.parameters_schema || {};
      
      const response = await customFunctions.test(func.id, testPayload);
      
      setTestResult({
        status: response.data.status_code,
        response: response.data.response,
        success: true
      });
    } catch (err) {
      setTestError(err.response?.data?.detail || err.message || 'Function test failed');
      setTestResult({
        success: false,
        error: err.response?.data?.detail || err.message || 'Function test failed'
      });
    } finally {
      setTestingFunction(false);
    }
  };

  const handleEditFunction = (func) => {
    // Set editing mode with function data
    setEditingFunctionData({
      id: func.id,
      name: func.name || '',
      description: func.description || '',
      trigger_event: func.trigger_event || 'call_started',
      webhook_url: func.webhook_url || '',
      http_method: func.http_method || 'POST',
      parameters_schema: typeof func.parameters_schema === 'string' 
        ? func.parameters_schema 
        : JSON.stringify(func.parameters_schema || {}, null, 2),
      system_prompt_modification: func.system_prompt_modification || '',
      greeting_modification: func.greeting_modification || '',
      is_active: func.is_active !== undefined ? func.is_active : true
    });
  };

  const copyFunctionAsJSON = async (func) => {
    try {
      const functionJSON = {
        name: func.name || '',
        description: func.description || '',
        trigger_event: func.trigger_event || 'call_started',
        target_agent_id: func.target_agent_id || editingAgent?.id || '',
        webhook_url: func.webhook_url || '',
        http_method: func.http_method || 'POST',
        parameters: typeof func.parameters_schema === 'string' 
          ? func.parameters_schema 
          : JSON.stringify(func.parameters_schema || {}, null, 2),
        system_prompt_modification: func.system_prompt_modification || '',
        greeting_modification: func.greeting_modification || '',
        is_active: func.is_active !== undefined ? func.is_active : true
      };

      const jsonString = JSON.stringify(functionJSON, null, 2);
      await navigator.clipboard.writeText(jsonString);
      
      setCopySuccess(true);
      setToast({ message: 'Function JSON copied to clipboard!', type: 'success' });
      setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
    } catch (err) {
      setToast({ message: 'Failed to copy function: ' + err.message, type: 'error' });
    }
  };

  const handleSaveFunction = async () => {
    if (!editingFunctionData) return;
    
    setSavingFunction(true);
    try {
      // Prepare update data - match exactly what CustomFunctions component sends
      const updateData = {
        name: editingFunctionData.name,
        description: editingFunctionData.description,
        trigger_event: editingFunctionData.trigger_event,
        webhook_url: editingFunctionData.webhook_url,
        http_method: editingFunctionData.http_method,
        // Send parameters_schema as a JSON string (not a dict)
        // The backend accepts either "parameters" or "parameters_schema"
        parameters: typeof editingFunctionData.parameters_schema === 'string' 
          ? editingFunctionData.parameters_schema 
          : JSON.stringify(editingFunctionData.parameters_schema || {}),
        system_prompt_modification: editingFunctionData.system_prompt_modification || '',
        greeting_modification: editingFunctionData.greeting_modification || '',
        is_active: editingFunctionData.is_active !== undefined ? editingFunctionData.is_active : true
      };
      
      await customFunctions.update(editingFunctionData.id, updateData);
      
      // Reload functions and update selectedFunction immediately
      const response = await customFunctions.list();
      const allFunctions = response.data.functions || [];
      const agentFunctions = allFunctions.filter(
        (func) => !func.target_agent_id || func.target_agent_id === editingAgent.id
      );
      
      // Update functions list
      setFunctions(agentFunctions);
      
      // Find and update the selected function immediately from the fresh data
      const updatedFunc = agentFunctions.find(f => f.id === editingFunctionData.id);
      if (updatedFunc) {
        setSelectedFunction(updatedFunc);
      }
      
      setToast({ message: 'Function updated successfully!', type: 'success' });
      setEditingFunctionData(null);
    } catch (err) {
      console.error('Failed to save function:', err);
      setToast({ 
        message: 'Failed to save function: ' + (err.response?.data?.detail || err.message), 
        type: 'error' 
      });
    } finally {
      setSavingFunction(false);
    }
  };

  const handleCancelEditFunction = () => {
    setEditingFunctionData(null);
  };

  const handleDeleteFunction = async () => {
    if (!deleteFunctionModal.functionId) return;
    
    try {
      await customFunctions.delete(deleteFunctionModal.functionId);
      setToast({ message: 'Function deleted successfully', type: 'success' });
      setDeleteFunctionModal({ open: false, functionId: null, functionName: '' });
      setSelectedFunction(null);
      await loadAgentFunctions();
    } catch (error) {
      console.error('Error deleting function:', error);
      setToast({ 
        message: error.response?.data?.detail || 'Failed to delete function', 
        type: 'error' 
      });
    }
  };

  const handleCreateFunction = () => {
    if (!editingAgent) {
      setToast({ message: 'Please save the agent first', type: 'error' });
      return;
    }
    setCreatingFunction(true);
    setSelectedFunction(null);
    setEditingFunctionData(null);
    setNewFunctionData({
      name: '',
      description: '',
      trigger_event: 'call_started',
      target_agent_id: editingAgent.id, // Automatically set to current agent
      webhook_url: '',
      http_method: 'POST',
      parameters: '{}',
      system_prompt_modification: '',
      greeting_modification: '',
      is_active: true
    });
  };

  const handleSaveNewFunction = async () => {
    if (!newFunctionData.name || !newFunctionData.webhook_url) {
      setToast({ message: 'Name and webhook URL are required', type: 'error' });
      return;
    }

    // Validate JSON
    try {
      JSON.parse(newFunctionData.parameters);
    } catch (e) {
      setToast({ message: 'Parameters must be valid JSON', type: 'error' });
      return;
    }

    setSavingFunction(true);
    try {
      const createData = {
        name: newFunctionData.name,
        description: newFunctionData.description,
        trigger_event: newFunctionData.trigger_event,
        target_agent_id: newFunctionData.target_agent_id || editingAgent.id,
        webhook_url: newFunctionData.webhook_url,
        http_method: newFunctionData.http_method,
        parameters: newFunctionData.parameters,
        system_prompt_modification: newFunctionData.system_prompt_modification || '',
        greeting_modification: newFunctionData.greeting_modification || '',
        is_active: newFunctionData.is_active !== undefined ? newFunctionData.is_active : true
      };

      await customFunctions.create(createData);
      
      // Reload functions
      await loadAgentFunctions();
      
      setToast({ message: 'Function created successfully!', type: 'success' });
      setCreatingFunction(false);
      setNewFunctionData({
        name: '',
        description: '',
        trigger_event: 'call_started',
        target_agent_id: '',
        webhook_url: '',
        http_method: 'POST',
        parameters: '{}',
        system_prompt_modification: '',
        greeting_modification: '',
        is_active: true
      });
    } catch (err) {
      setToast({ message: 'Failed to create function: ' + (err.response?.data?.detail || err.message), type: 'error' });
    } finally {
      setSavingFunction(false);
    }
  };

  const handleCancelCreateFunction = () => {
    setCreatingFunction(false);
    setPasteError('');
    setNewFunctionData({
      name: '',
      description: '',
      trigger_event: 'call_started',
      target_agent_id: '',
      webhook_url: '',
      http_method: 'POST',
      parameters: '{}',
      system_prompt_modification: '',
      greeting_modification: '',
      is_active: true
    });
  };

  const copyNewFunctionAsJSON = async () => {
    try {
      const functionJSON = {
        name: newFunctionData.name || '',
        description: newFunctionData.description || '',
        trigger_event: newFunctionData.trigger_event || 'call_started',
        target_agent_id: newFunctionData.target_agent_id || editingAgent?.id || '',
        webhook_url: newFunctionData.webhook_url || '',
        http_method: newFunctionData.http_method || 'POST',
        parameters: newFunctionData.parameters || '{}',
        system_prompt_modification: newFunctionData.system_prompt_modification || '',
        greeting_modification: newFunctionData.greeting_modification || '',
        is_active: newFunctionData.is_active !== undefined ? newFunctionData.is_active : true
      };

      const jsonString = JSON.stringify(functionJSON, null, 2);
      await navigator.clipboard.writeText(jsonString);
      
      setCopySuccess(true);
      setToast({ message: 'Function JSON copied to clipboard!', type: 'success' });
      setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
    } catch (err) {
      setToast({ message: 'Failed to copy function: ' + err.message, type: 'error' });
    }
  };

  const pasteNewFunctionFromJSON = async () => {
    try {
      setPasteError('');
      setToast(null);
      
      // Read from clipboard
      const clipboardText = await navigator.clipboard.readText();
      
      // Parse JSON
      let parsedData;
      try {
        parsedData = JSON.parse(clipboardText);
      } catch (e) {
        setPasteError('Invalid JSON format. Please check your clipboard content.');
        return;
      }

      // Validate required fields
      if (!parsedData.name || !parsedData.webhook_url) {
        setPasteError('JSON must include "name" and "webhook_url" fields.');
        return;
      }

      // Map parsed data to form fields
      const mappedData = {
        name: parsedData.name || '',
        description: parsedData.description || '',
        trigger_event: parsedData.trigger_event || 'call_started',
        target_agent_id: parsedData.target_agent_id || editingAgent?.id || '',
        webhook_url: parsedData.webhook_url || '',
        http_method: parsedData.http_method || 'POST',
        parameters: typeof parsedData.parameters === 'string' 
          ? parsedData.parameters 
          : JSON.stringify(parsedData.parameters || {}, null, 2),
        system_prompt_modification: parsedData.system_prompt_modification || '',
        greeting_modification: parsedData.greeting_modification || '',
        is_active: parsedData.is_active !== undefined ? parsedData.is_active : true
      };

      setNewFunctionData(mappedData);
      setToast({ message: 'Function data pasted from clipboard!', type: 'success' });
      
      setTimeout(() => {
        setToast(null);
      }, 3000);
    } catch (err) {
      setPasteError('Failed to paste function: ' + err.message);
    }
  };

  // Knowledge Base functions
  const loadAgentDocuments = async () => {
    if (!editingAgent) return;
    
    setLoadingKbDocuments(true);
    try {
      const response = await knowledgeBase.listDocuments();
      const allDocuments = response.data || [];
      // Filter documents for this agent
      const agentDocuments = allDocuments.filter(doc => doc.agent_id === editingAgent.id);
      setKbDocuments(agentDocuments);
    } catch (error) {
      console.error('Error loading documents:', error);
      setToast({ message: 'Failed to load documents', type: 'error' });
    } finally {
      setLoadingKbDocuments(false);
    }
  };

  const handleKbUpload = async (e) => {
    e.preventDefault();
    
    if (!kbSelectedFile || !editingAgent) {
      setToast({ message: 'Please select a file to upload', type: 'error' });
      return;
    }

    setKbUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', kbSelectedFile);
      if (kbUploadTitle) formData.append('title', kbUploadTitle);
      formData.append('agent_id', editingAgent.id);

      await knowledgeBase.uploadDocument(formData);
      setToast({ message: 'Document uploaded successfully', type: 'success' });
      setKbSelectedFile(null);
      setKbUploadTitle('');
      loadAgentDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      setToast({ message: 'Failed to upload document: ' + (error.response?.data?.detail || error.message), type: 'error' });
    } finally {
      setKbUploading(false);
    }
  };

  const handleKbQuery = async (e) => {
    e.preventDefault();
    if (!kbQueryText.trim() || !editingAgent) return;

    setKbQuerying(true);
    try {
      const response = await knowledgeBase.query({
        query: kbQueryText,
        agent_id: editingAgent.id,
        top_k: 5,
        score_threshold: 0.3
      });
      setKbQueryResults(response.data.results || []);
    } catch (error) {
      console.error('Error querying knowledge base:', error);
      setToast({ message: 'Failed to query knowledge base: ' + (error.response?.data?.detail || error.message), type: 'error' });
    } finally {
      setKbQuerying(false);
    }
  };

  const handleKbDeleteDocument = (docId) => {
    setKbDeleteModal({ show: true, docId });
  };

  const confirmKbDelete = async () => {
    if (!kbDeleteModal.docId) return;

    try {
      await knowledgeBase.deleteDocument(kbDeleteModal.docId);
      setToast({ message: 'Document deleted successfully', type: 'success' });
      loadAgentDocuments();
      setKbDeleteModal({ show: false, docId: null });
    } catch (error) {
      console.error('Error deleting document:', error);
      setToast({ message: 'Failed to delete document: ' + (error.response?.data?.detail || error.message), type: 'error' });
      setKbDeleteModal({ show: false, docId: null });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'processing': return 'text-warning';
      case 'failed': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'processing': return Clock;
      case 'failed': return XCircle;
      default: return FileText;
    }
  };

  // Outbound Test functions
  const loadAgentPhoneNumbers = async () => {
    if (!editingAgent) return;
    
    setLoadingPhoneNumbers(true);
    try {
      const response = await api.get('/numbers');
      const allNumbers = response.data || [];
      // Filter phone numbers for this agent
      const agentNumbers = allNumbers.filter(num => num.agent_id === editingAgent.id);
      setOutboundPhoneNumbers(agentNumbers);
      
      // Auto-select first phone number if available
      if (agentNumbers.length > 0 && !outboundTestCall.from_number) {
        setOutboundTestCall({ ...outboundTestCall, from_number: agentNumbers[0].e164, agent_id: editingAgent.id });
      } else {
        setOutboundTestCall({ ...outboundTestCall, agent_id: editingAgent.id });
      }
    } catch (error) {
      console.error('Error loading phone numbers:', error);
      setToast({ message: 'Failed to load phone numbers', type: 'error' });
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  const loadExtractionFields = async () => {
    if (!editingAgent) return;
    
    setLoadingExtractionFields(true);
    try {
      const response = await agents.listExtractionFields(editingAgent.id);
      setExtractionFields(response.data || []);
    } catch (error) {
      console.error('Error loading extraction fields:', error);
      setToast({ message: 'Failed to load extraction fields', type: 'error' });
    } finally {
      setLoadingExtractionFields(false);
    }
  };

  const handleSaveExtractionField = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!editingAgent) return;
    
    try {
      // Convert choices array to JSON string and ensure proper types before saving
      const fieldData = {
        name: newExtractionField.name,
        description: newExtractionField.description,
        data_type: newExtractionField.data_type,
        skip_if_not_found: Boolean(newExtractionField.skip_if_not_found),
        format_example: newExtractionField.format_example || null,
        choices: newExtractionField.data_type === 'selector' && newExtractionField.choices.length > 0
          ? JSON.stringify(newExtractionField.choices.filter(c => c.trim() !== ''))
          : newExtractionField.data_type === 'selector' ? null : null,
        display_order: Number(newExtractionField.display_order) || 0,
        is_active: Boolean(newExtractionField.is_active)
      };
      
      if (editingExtractionField) {
        // Update existing field
        await agents.updateExtractionField(editingAgent.id, editingExtractionField.id, fieldData);
        setToast({ message: 'Extraction field updated successfully', type: 'success' });
      } else {
        // Create new field
        await agents.createExtractionField(editingAgent.id, fieldData);
        setToast({ message: 'Extraction field created successfully', type: 'success' });
      }
      setShowExtractionFieldForm(false);
      setEditingExtractionField(null);
      setNewExtractionField({
        name: '',
        description: '',
        data_type: 'text',
        skip_if_not_found: false,
        format_example: '',
        choices: [],
        display_order: extractionFields.length,
        is_active: true
      });
      loadExtractionFields();
    } catch (error) {
      console.error('Error saving extraction field:', error);
      
      // Handle Pydantic validation errors (422) which return an array of error objects
      let errorMessage = 'Failed to save extraction field';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Format validation errors into readable message
          errorMessage = detail.map(err => {
            const field = err.loc && err.loc.length > 1 ? err.loc[err.loc.length - 1] : 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      setToast({ message: errorMessage, type: 'error' });
    }
  };

  const handleEditExtractionField = (field) => {
    setEditingExtractionField(field);
    // Parse choices JSON string to array
    let choicesArray = [];
    if (field.choices) {
      try {
        const parsed = JSON.parse(field.choices);
        if (Array.isArray(parsed)) {
          choicesArray = parsed;
        }
      } catch (e) {
        // If parsing fails, treat as empty array
        choicesArray = [];
      }
    }
    
    setNewExtractionField({
      name: field.name,
      description: field.description,
      data_type: field.data_type,
      skip_if_not_found: field.skip_if_not_found,
      format_example: field.format_example || '',
      choices: choicesArray,
      display_order: field.display_order,
      is_active: field.is_active
    });
    setShowExtractionFieldForm(true);
  };
  
  const handleAddChoice = () => {
    setNewExtractionField({
      ...newExtractionField,
      choices: [...newExtractionField.choices, '']
    });
  };
  
  const handleRemoveChoice = (index) => {
    const newChoices = newExtractionField.choices.filter((_, i) => i !== index);
    setNewExtractionField({
      ...newExtractionField,
      choices: newChoices
    });
  };
  
  const handleChoiceChange = (index, value) => {
    const newChoices = [...newExtractionField.choices];
    newChoices[index] = value;
    setNewExtractionField({
      ...newExtractionField,
      choices: newChoices
    });
  };

  const handleDeleteExtractionField = (fieldId, fieldName) => {
    setExtractionFieldDeleteModal({ show: true, fieldId, fieldName });
  };

  const confirmExtractionFieldDelete = async () => {
    if (!editingAgent || !extractionFieldDeleteModal.fieldId) return;
    
    try {
      await agents.deleteExtractionField(editingAgent.id, extractionFieldDeleteModal.fieldId);
      setToast({ message: 'Extraction field deleted successfully', type: 'success' });
      setExtractionFieldDeleteModal({ show: false, fieldId: null, fieldName: '' });
      loadExtractionFields();
    } catch (error) {
      console.error('Error deleting extraction field:', error);
      setToast({ message: error.response?.data?.detail || 'Failed to delete extraction field', type: 'error' });
    }
  };

  const handleOutboundTestCall = async (e) => {
    e.preventDefault();
    
    if (!outboundTestCall.from_number || !outboundTestCall.to_number || !editingAgent) {
      setToast({ message: 'Please fill in all required fields', type: 'error' });
      return;
    }

    setOutboundCalling(true);
    try {
      const variables = kvToObject(outboundVars);
      const overrides = kvToObject(outboundOverrideVars);
      
      const callRequest = {
        from_number: outboundTestCall.from_number,
        to_number: outboundTestCall.to_number,
        agent_id: editingAgent.id,
        variables,
        overrides
      };
      
      const response = await api.post('/outbound/test-call', callRequest);
      setToast({ message: `Outbound call initiated: ${response.data.message || 'Success'}`, type: 'success' });
      setOutboundTestCall({ ...outboundTestCall, to_number: '' });
    } catch (error) {
      console.error('Error making outbound call:', error);
      setToast({ message: 'Failed to make outbound call: ' + (error.response?.data?.detail || error.message), type: 'error' });
    } finally {
      setOutboundCalling(false);
    }
  };

  const setOutboundExampleVariables = () => {
    updateOutboundVars([
      { key: 'customer_name', value: 'John Doe' },
      { key: 'account_id', value: '12345' },
      { key: 'call_reason', value: 'Follow-up' },
    ]);
  };

  const setOutboundExampleOverrides = () => {
    updateOutboundOverrideVars([
      { key: 'system_prompt', value: 'You are calling to follow up on a customer inquiry.' },
      { key: 'greeting', value: 'Hello, this is calling from our company.' },
    ]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Update backchanneling_words from local input state before submitting
    let updatedFormData = { ...formData };
    if (activeTab === 'backchanneling') {
      const value = backchannelingWordsInput.trim();
      if (value === '') {
        updatedFormData.backchanneling_words = null;
      } else {
        // Split by comma and clean up
        const words = value.split(',').map(w => w.trim()).filter(w => w.length > 0);
        updatedFormData.backchanneling_words = words.length > 0 ? JSON.stringify(words) : null;
      }
    }
    
    // Ensure agent_transfer_enabled is always included and is a proper boolean
    if (updatedFormData.agent_transfer_enabled === undefined || updatedFormData.agent_transfer_enabled === null) {
      updatedFormData.agent_transfer_enabled = false;
    } else {
      // Force boolean conversion
      updatedFormData.agent_transfer_enabled = Boolean(updatedFormData.agent_transfer_enabled);
    }
    // Ensure agent_transfer_agents is null if empty string or undefined
    if (updatedFormData.agent_transfer_agents === '' || updatedFormData.agent_transfer_agents === undefined) {
      updatedFormData.agent_transfer_agents = null;
    }
    
    // Calculate final list of active types and send ONLY that
    if (updatedFormData.pii_policy !== 'off') {
      const defaultTypes = {
        'name': '[NAME]',
        'email': '[EMAIL]',
        'phone': '[PHONE]',
        'ssn': '[SSN]',
        'credit_card': '[CREDIT_CARD]',
        'address': '[ADDRESS]',
        'date_of_birth': '[DATE_OF_BIRTH]',
        'driver_license': '[DRIVER_LICENSE]',
        'passport': '[PASSPORT]',
        'bank_account': '[BANK_ACCOUNT]',
        'ip_address': '[IP_ADDRESS]',
        'medical_record': '[MEDICAL_RECORD]',
        'insurance_id': '[INSURANCE_ID]'
      };
      
      // Remove disabled defaults
      const activeDefaults = {};
      for (const [type, placeholder] of Object.entries(defaultTypes)) {
        if (!disabledDefaultTypes.includes(type)) {
          activeDefaults[type] = placeholder;
        }
      }
      
      // Merge with custom types - this is the FINAL list
      const finalActiveTypes = { ...activeDefaults, ...customPiiTypes };
      
      // Save the final list (no _disabled_defaults, just the final types)
      updatedFormData.custom_pii_types = JSON.stringify(finalActiveTypes);
    }
    
    // Flush pending flow data from FlowBuilder before save (avoids debounce race - flow edits
    // are debounced 150ms, so Save clicked quickly could send stale conversation_flow)
    if (formData.agent_type === 'flow' && flowBuilderRef.current?.getCurrentFlow) {
      const latestFlow = flowBuilderRef.current.getCurrentFlow();
      if (latestFlow) {
        updatedFormData.conversation_flow = latestFlow;
      }
    }

    // Update formData state with all changes before submission
    setFormData(updatedFormData);
    
    // Pass updatedFormData so parent uses it (avoids async setState race)
    setTimeout(() => {
      onSubmit(e, updatedFormData);
    }, 0);
  };

  const tabs = [
    { id: 'basic', label: 'Basic & AI', icon: Bot },
    { id: 'flow', label: 'Conversation Flow', icon: GitBranch },
    { id: 'voice', label: 'Voice', icon: Mic },
    { id: 'functions', label: 'Functions', icon: Code },
    { id: 'pii', label: 'PII', icon: Shield },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'transfer', label: 'Call Transfer', icon: PhoneForwarded },
    { id: 'agent-transfer', label: 'Agent Transfer', icon: Bot },
    { id: 'backchanneling', label: 'Backchanneling', icon: MessageSquare },
    { id: 'data-extraction', label: 'Data Extraction', icon: Database },
    { id: 'test', label: 'Test', icon: FlaskConical },
    { id: 'chat-test', label: 'Chat Test', icon: MessageSquare },
    { id: 'background-noises', label: 'Background Noises', icon: Music },
    { id: 'noise-cancellation', label: 'Noise Cancellation', icon: Mic2 },
    { id: 'ai-acoustics', label: 'AI Acoustics', icon: Sparkles },
    { id: 'reminders', label: 'Reminders', icon: Clock },
    { id: 'agent-end-call', label: 'Agent End Call', icon: PhoneOff },
    { id: 'call-limits', label: 'Call Limits', icon: Timer },
    { id: 'voicemail-drop', label: 'Voicemail Drop', icon: Voicemail },
    { id: 'dtmf', label: 'DTMF Keypad', icon: Hash },
    { id: 'outbound-test', label: 'Outbound Test', icon: PhoneCall },
    { id: 'knowledge-base', label: 'Knowledge Base', icon: BookOpen },
    { id: 'liveavatar', label: 'LiveAvatar', icon: UserCircle },
    { id: 'product-images', label: 'Product Images', icon: Image },
    { id: 'default-variables', label: 'Default Variables', icon: Zap },
  ];

  return (
    <div className="w-full flex flex-col bg-card border border-border rounded-xl" style={{ minHeight: 'calc(100vh - 100px)' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div>
          <h3 className="text-2xl font-bold text-foreground">
            {editingAgent ? 'Edit Agent' : 'Create New Agent'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Update prompts, model, voice, and configuration
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="group p-2 hover:bg-muted rounded-lg transition-all duration-300 text-muted-foreground hover:text-foreground hover:scale-110 hover:shadow-lg hover:shadow-primary/10"
          >
            <X className="w-5 h-5 transition-transform duration-300 group-hover:rotate-90" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-10 px-6 py-4 border-b border-border bg-card">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group relative flex items-center justify-center gap-2 px-3 py-2.5 
                  text-sm font-medium transition-all duration-300 rounded-lg
                  ${
                    activeTab === tab.id
                      ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
                      : 'bg-background/50 text-muted-foreground hover:text-foreground hover:bg-background hover:shadow-md hover:shadow-primary/5 border border-border/50 hover:border-primary/30'
                  }
                `}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${
                  activeTab === tab.id ? 'scale-110' : 'group-hover:scale-110'
                }`} />
                <span className="whitespace-nowrap truncate">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {activeTab === 'functions' ? (
        <div className="flex-1 flex gap-6 p-6 min-h-0 overflow-hidden">
          {/* Left Side - Functions List */}
          <div className="w-80 flex-shrink-0 border-r border-border pr-6 overflow-y-auto animate-slide-in-left"
            style={{ 
              scrollBehavior: 'smooth',
              maxHeight: 'calc(100vh - 280px)'
            }}
          >
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
              <Settings className="w-5 h-5 text-primary" />
              <h4 className="text-lg font-semibold text-foreground">Functions & Integrations</h4>
              </div>
              {editingAgent && (
                <button
                  type="button"
                  onClick={handleCreateFunction}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/20"
                  title="Create New Function"
                >
                  <Plus className="w-4 h-4" />
                  Create Function
                </button>
              )}
            </div>

            {loadingFunctions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : functions.length === 0 && !creatingFunction ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">
                    {editingAgent ? 'No functions mapped to this agent' : 'Save agent first to add functions'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {functions.map((func) => (
                    <button
                      key={func.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedFunction(func);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-all duration-300 ${
                        selectedFunction?.id === func.id
                          ? 'border-primary bg-primary/10 shadow-md shadow-primary/10'
                          : 'border-border hover:bg-muted/50 hover:border-primary/30 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/5'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">{func.name}</span>
                        {func.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-primary/20 text-primary">
                            Enabled
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{func.description || 'No description'}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Side - Function Configuration */}
            <div className="flex-1 overflow-y-auto min-h-0 animate-slide-in-right animate-delay-100"
              style={{ 
                scrollBehavior: 'smooth',
                maxHeight: 'calc(100vh - 280px)'
              }}
            >
              {creatingFunction ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-semibold text-foreground">Create New Function</h4>
                      <p className="text-sm text-muted-foreground">This function will be automatically assigned to this agent</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={pasteNewFunctionFromJSON}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10 border border-border"
                        title="Paste function from JSON"
                      >
                        <Clipboard className="w-4 h-4" />
                        Paste JSON
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelCreateFunction}
                        className="px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  {pasteError && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-destructive" />
                      <p className="text-destructive text-sm">{pasteError}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Function Name */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Function Name *</label>
                      <input
                        type="text"
                        value={newFunctionData.name}
                        onChange={(e) => setNewFunctionData({ ...newFunctionData, name: e.target.value })}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="e.g., CRM Customer Lookup"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                      <textarea
                        value={newFunctionData.description}
                        onChange={(e) => setNewFunctionData({ ...newFunctionData, description: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        placeholder="What does this function do?"
                      />
                    </div>

                    {/* Trigger Event */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Trigger Event</label>
                      <AnimatedDropdown
                        value={newFunctionData.trigger_event}
                        onChange={(value) => setNewFunctionData({ ...newFunctionData, trigger_event: value })}
                        options={[
                          { value: 'call_started', label: 'Call Started - Before agent speaks' },
                          { value: 'call_ended', label: 'Call Ended - After call finishes' },
                          { value: 'agent_response', label: 'Agent Response - After agent speaks' },
                          { value: 'agent_tool', label: 'Agent Tool - Agent calls it and waits for the result' },
                          { value: 'call_analyzed', label: 'Call Analyzed - After AI analysis completes' }
                        ]}
                        placeholder="Select trigger event"
                      />
                    </div>

                    {/* Webhook URL */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Webhook URL *</label>
                      <input
                        type="url"
                        value={newFunctionData.webhook_url}
                        onChange={(e) => setNewFunctionData({ ...newFunctionData, webhook_url: e.target.value })}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="https://integration.braincx.ai/webhook/crm-lookup"
                      />
                    </div>

                    {/* HTTP Method */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">HTTP Method</label>
                      <AnimatedDropdown
                        value={newFunctionData.http_method}
                        onChange={(value) => setNewFunctionData({ ...newFunctionData, http_method: value })}
                        options={[
                          { value: 'POST', label: 'POST' },
                          { value: 'GET', label: 'GET' },
                          { value: 'PUT', label: 'PUT' },
                          { value: 'PATCH', label: 'PATCH' },
                          { value: 'DELETE', label: 'DELETE' }
                        ]}
                        placeholder="Select HTTP method"
                      />
                    </div>

                    {/* Parameters */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Parameters (JSON)</label>
                      <textarea
                        value={newFunctionData.parameters}
                        onChange={(e) => setNewFunctionData({ ...newFunctionData, parameters: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder='{"lookup_type": "phone", "include_history": true}'
                      />
                    </div>

                    {/* System Prompt Modification */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">System Prompt Modification</label>
                      <textarea
                        value={newFunctionData.system_prompt_modification}
                        onChange={(e) => setNewFunctionData({ ...newFunctionData, system_prompt_modification: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        placeholder="Additional instructions to add to the agent's system prompt based on the function result"
                      />
                    </div>

                    {/* Greeting Modification */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Greeting Modification</label>
                      <textarea
                        value={newFunctionData.greeting_modification}
                        onChange={(e) => setNewFunctionData({ ...newFunctionData, greeting_modification: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        placeholder="How should the agent modify its greeting based on the function result?"
                      />
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-background/30 border border-border/40">
                      {newFunctionData.is_active ? (
                        <ToggleRight className="w-5 h-5 text-success cursor-pointer" onClick={() => setNewFunctionData({ ...newFunctionData, is_active: false })} />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-muted-foreground cursor-pointer" onClick={() => setNewFunctionData({ ...newFunctionData, is_active: true })} />
                      )}
                      <span className="text-sm text-foreground font-medium">Function is active</span>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end gap-2 pt-3 border-t border-border/40">
                      <button
                        type="button"
                        onClick={handleCancelCreateFunction}
                        className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveNewFunction}
                        disabled={savingFunction || !newFunctionData.name || !newFunctionData.webhook_url}
                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        {savingFunction ? 'Creating...' : 'Create Function'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : selectedFunction ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-semibold text-foreground">Configuration</h4>
                      <p className="text-sm text-muted-foreground">Function settings and parameters</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDeleteFunctionModal({ 
                            open: true, 
                            functionId: selectedFunction.id, 
                            functionName: selectedFunction.name 
                          });
                        }}
                        className="px-3 py-1.5 text-sm bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md border border-destructive/20 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFunction(null);
                        }}
                        className="px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Action Buttons */}
                    <div className="flex items-center gap-3 mb-4">
                      {!editingFunctionData ? (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTestFunction(selectedFunction);
                            }}
                            disabled={testingFunction}
                             className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
                          >
                            <Play className="w-4 h-4" />
                            {testingFunction ? 'Testing...' : 'Test Function'}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEditFunction(selectedFunction);
                            }}
                             className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10 border border-border hover:border-primary/30"
                          >
                            <Edit3 className="w-4 h-4" />
                            Edit Function
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyFunctionAsJSON(selectedFunction);
                            }}
                             className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10 border border-border hover:border-primary/30"
                          >
                            <Copy className="w-4 h-4" />
                            Copy JSON
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSaveFunction();
                            }}
                            disabled={savingFunction}
                             className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
                          >
                            {savingFunction ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleCancelEditFunction();
                            }}
                             className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10 border border-border hover:border-primary/30"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>

                    {/* Test Result */}
                    {testResult && (
                      <div className={`p-4 rounded-lg border ${
                        testResult.success 
                          ? 'bg-primary/10 border-primary/30' 
                          : 'bg-red-500/10 border-red-500/30'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-semibold ${
                            testResult.success ? 'text-primary' : 'text-red-400'
                          }`}>
                            {testResult.success ? 'Test Successful' : 'Test Failed'}
                          </span>
                        </div>
                        {testResult.success ? (
                          <div className="space-y-2">
                            <div className="text-sm text-foreground">
                              <span className="font-medium">Status Code:</span> {testResult.status}
                            </div>
                            <div className="text-sm text-foreground">
                              <span className="font-medium">Response:</span>
                              <pre className="mt-1 p-2 bg-background/50 rounded text-xs overflow-auto">
                                {typeof testResult.response === 'string' 
                                  ? testResult.response 
                                  : JSON.stringify(testResult.response, null, 2)}
                              </pre>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-red-400">{testResult.error}</div>
                        )}
                      </div>
                    )}

                    {/* Row 1: Function Name + Description */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Function Name */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Function Name</label>
                        {editingFunctionData ? (
                          <input
                            type="text"
                            value={editingFunctionData.name}
                            onChange={(e) => setEditingFunctionData({ ...editingFunctionData, name: e.target.value })}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        ) : (
                          <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                            <span className="text-sm text-foreground">{selectedFunction.name}</span>
                          </div>
                        )}
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                        {editingFunctionData ? (
                          <input
                            type="text"
                            value={editingFunctionData.description}
                            onChange={(e) => setEditingFunctionData({ ...editingFunctionData, description: e.target.value })}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        ) : (
                          <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                            <span className="text-sm text-foreground">{selectedFunction.description || 'No description'}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Trigger Event + HTTP Method */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Trigger Event */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Trigger Event</label>
                        <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                          <span className="text-sm text-foreground capitalize">{selectedFunction.trigger_event?.replace('_', ' ')}</span>
                        </div>
                      </div>

                      {/* HTTP Method */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">HTTP Method</label>
                        {editingFunctionData ? (
                          <AnimatedDropdown
                            value={editingFunctionData.http_method}
                            onChange={(value) => setEditingFunctionData({ ...editingFunctionData, http_method: value })}
                            options={[
                              { value: 'POST', label: 'POST' },
                              { value: 'GET', label: 'GET' },
                              { value: 'PUT', label: 'PUT' },
                              { value: 'PATCH', label: 'PATCH' },
                              { value: 'DELETE', label: 'DELETE' }
                            ]}
                            placeholder="Select HTTP method"
                            className="text-sm"
                          />
                        ) : (
                          <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                            <span className="text-sm text-foreground">{selectedFunction.http_method}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 3: Webhook URL (full width) */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Webhook URL</label>
                      {editingFunctionData ? (
                        <input
                          type="text"
                          value={editingFunctionData.webhook_url}
                          onChange={(e) => setEditingFunctionData({ ...editingFunctionData, webhook_url: e.target.value })}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                          <span className="text-sm text-foreground break-all">{selectedFunction.webhook_url}</span>
                        </div>
                      )}
                    </div>

                    {/* System Prompt Modification */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">System Prompt Modification</label>
                      {editingFunctionData ? (
                        <textarea
                          value={editingFunctionData.system_prompt_modification}
                          onChange={(e) => setEditingFunctionData({ ...editingFunctionData, system_prompt_modification: e.target.value })}
                          rows={4}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                          placeholder="Enter system prompt modification..."
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                          <p className="text-sm text-foreground whitespace-pre-wrap">{selectedFunction.system_prompt_modification || 'No modification'}</p>
                        </div>
                      )}
                    </div>

                    {/* Greeting Modification */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Greeting Modification</label>
                      {editingFunctionData ? (
                        <textarea
                          value={editingFunctionData.greeting_modification}
                          onChange={(e) => setEditingFunctionData({ ...editingFunctionData, greeting_modification: e.target.value })}
                          rows={4}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                          placeholder="Enter greeting modification..."
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                          <p className="text-sm text-foreground whitespace-pre-wrap">{selectedFunction.greeting_modification || 'No modification'}</p>
                        </div>
                      )}
                    </div>

                    {/* Parameters */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Parameters Schema (JSON)</label>
                      {editingFunctionData ? (
                        <textarea
                          value={editingFunctionData.parameters_schema}
                          onChange={(e) => setEditingFunctionData({ ...editingFunctionData, parameters_schema: e.target.value })}
                          rows={6}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                          placeholder='{"key": "value"}'
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                          <pre className="text-xs text-foreground font-mono overflow-auto">
                            {typeof selectedFunction.parameters_schema === 'string' 
                              ? selectedFunction.parameters_schema 
                              : JSON.stringify(selectedFunction.parameters_schema || {}, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                      {editingFunctionData ? (
                        <AnimatedCheckbox
                          id="function-is-active"
                          checked={editingFunctionData.is_active}
                          onChange={(e) => setEditingFunctionData({ ...editingFunctionData, is_active: e.target.checked })}
                          label={editingFunctionData.is_active ? 'Enabled' : 'Disabled'}
                          size="md"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted/20 border border-border rounded-lg">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            selectedFunction.is_active 
                              ? 'bg-primary/20 text-primary' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {selectedFunction.is_active ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-sm text-muted-foreground">Select a function to view its configuration</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'test' ? (
          <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
            <div className="px-6 pt-6">
              <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Don't send webhooks
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Test sessions started from the Test and Chat Test tabs will not send any webhooks
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={suppressTestWebhooks}
                  onClick={() => setSuppressTestWebhooks(v => !v)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    suppressTestWebhooks ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      suppressTestWebhooks ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
            <TestAgentInterfaceInline
              suppressWebhooks={suppressTestWebhooks}
              agent={{
                id: editingAgent?.id || 'temp',
                name: formData.name || 'Test Agent',
                llm_model: formData.llm_model || 'gpt-4o-mini',
                voice_provider: formData.voice_provider || 'cartesia',
                temperature: formData.temperature ?? 0.7,
                voice_temperature: formData.voice_temperature ?? 0.7,
                voice_stability: formData.voice_stability ?? 0.6,
                voice_volume: formData.voice_volume ?? 1,
                elevenlabs_speed: formData.elevenlabs_speed ?? 1.0,
                elevenlabs_voice_id: formData.elevenlabs_voice_id,
                elevenlabs_model: formData.elevenlabs_model || 'eleven_turbo_v2_5',
                cartesia_voice_id: formData.cartesia_voice_id,
                sonic_voice_id: formData.sonic_voice_id,
                sonic_model: formData.sonic_model || 'sonic-3',
                cartesia_language: formData.cartesia_language || null,
                fishaudio_voice_id: formData.fishaudio_voice_id,
                fishaudio_model: formData.fishaudio_model,
                stt_model: formData.stt_model || 'nova3_mono_en',
                stt_language: formData.stt_language || 'en-US',
              }}
            />
            </div>
          </div>
        ) : activeTab === 'chat-test' ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: '24px', gap: '16px' }}>
            <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Don't send webhooks
                </label>
                <p className="text-xs text-muted-foreground">
                  Test sessions started from the Test and Chat Test tabs will not send any webhooks
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={suppressTestWebhooks}
                onClick={() => setSuppressTestWebhooks(v => !v)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  suppressTestWebhooks ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    suppressTestWebhooks ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {editingAgent?.id ? (
              <div style={{ flex: 1, minHeight: 0 }}>
                <ChatEmbedWidget agentId={editingAgent.id} suppressWebhooks={suppressTestWebhooks} />
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted-foreground)' }}>
                Save the agent first to test the chat.
              </div>
            )}
          </div>
        ) : activeTab === 'background-noises' ? (
          <BackgroundNoisesTab
            agent={editingAgent}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
          />
        ) : activeTab === 'noise-cancellation' ? (
          <NoiseCancellationTab
            agent={editingAgent}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
          />
        ) : activeTab === 'ai-acoustics' ? (
          <AiAcousticsTab
            agent={editingAgent}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
          />
        ) : activeTab === 'reminders' ? (
          <RemindersTab
            agent={editingAgent}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
          />
        ) : activeTab === 'call-limits' ? (
          <CallLimitsTab
            formData={formData}
            setFormData={setFormData}
          />
        ) : activeTab === 'agent-end-call' ? (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <AgentEndCallTab
              agent={editingAgent}
              formData={formData}
              setFormData={setFormData}
            />
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30"
              >
                {editingAgent ? 'Save Agent' : 'Create Agent'}
              </button>
            </div>
          </form>
        ) : activeTab === 'dtmf' ? (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <DtmfTab
              agent={editingAgent}
              formData={formData}
              setFormData={setFormData}
            />
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30"
              >
                {editingAgent ? 'Save Agent' : 'Create Agent'}
              </button>
            </div>
          </form>
        ) : activeTab === 'voicemail-drop' ? (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <VoicemailDropTab
              agent={editingAgent}
              formData={formData}
              setFormData={setFormData}
            />
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30"
              >
                {editingAgent ? 'Save Agent' : 'Create Agent'}
              </button>
            </div>
          </form>
        ) : activeTab === 'outbound-test' ? (
          <div className="flex-1 overflow-y-auto min-h-0"
            style={{ 
              scrollBehavior: 'smooth',
              maxHeight: 'calc(100vh - 280px)'
            }}
          >
            {!editingAgent ? (
              <div className="flex items-center justify-center h-full p-6">
                <div className="text-center">
                  <PhoneCall className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Save agent first to test outbound calls</p>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <PhoneCall className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">Test Outbound Call</h3>
                      <p className="text-sm text-muted-foreground">Make an outbound call using this agent</p>
                    </div>
                  </div>
                  
                  <form onSubmit={handleOutboundTestCall} className="space-y-6">
                    {/* Phone Numbers */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          From Number (Caller ID)
                        </label>
                        {loadingPhoneNumbers ? (
                          <div className="flex items-center justify-center py-3">
                            <LoadingSpinner size="sm" />
                          </div>
                        ) : outboundPhoneNumbers.length === 0 ? (
                          <div className="px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-muted-foreground text-sm">
                            No phone numbers assigned to this agent
                          </div>
                        ) : (
                          <AnimatedDropdown
                            value={outboundTestCall.from_number}
                            onChange={(value) => setOutboundTestCall({ ...outboundTestCall, from_number: value })}
                            options={[
                              { value: '', label: 'Select caller ID number' },
                              ...outboundPhoneNumbers.map(phone => ({ value: phone.e164, label: phone.e164 }))
                            ]}
                            placeholder="Select caller ID number"
                            icon={Phone}
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                          <PhoneCall className="w-4 h-4" />
                          To Number (Target)
                        </label>
                        <input
                          type="tel"
                          value={outboundTestCall.to_number}
                          onChange={(e) => setOutboundTestCall({ ...outboundTestCall, to_number: e.target.value })}
                          placeholder="+12137771235"
                          className="w-full bg-muted/50 border border-border/60 rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                          required
                        />
                      </div>
                    </div>

                    {/* Variables & Overrides - Persistent Key-Value Editors */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Variables */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-foreground flex items-center gap-2">
                            <Variable className="w-4 h-4" />
                            Variables
                            <span className="text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded font-normal">persisted</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setShowOutboundVarsJson(p => !p)}
                              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showOutboundVarsJson ? 'Hide JSON' : 'Show JSON'}
                            </button>
                            <button
                              type="button"
                              onClick={setOutboundExampleVariables}
                              className="text-xs px-2 py-1 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg transition-all duration-300 flex items-center gap-1 font-medium"
                            >
                              <Zap className="w-3 h-3" />
                              Example
                            </button>
                            <button
                              type="button"
                              onClick={() => updateOutboundVars(prev => [...prev, { key: '', value: '' }])}
                              className="text-xs px-2 py-1 text-primary hover:text-primary/80 transition-colors font-medium flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Add
                            </button>
                          </div>
                        </div>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                          {outboundVars.map((v, i) => (
                            <div key={i} className="flex items-center gap-1.5 group">
                              <input
                                type="text"
                                value={v.key}
                                onChange={(e) => updateOutboundVars(prev => prev.map((item, idx) => idx === i ? { ...item, key: e.target.value } : item))}
                                placeholder="variable_name"
                                className="flex-1 min-w-0 font-mono text-xs bg-muted/30 border border-border/60 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-foreground"
                              />
                              <span className="text-muted-foreground text-xs">=</span>
                              <input
                                type="text"
                                value={v.value}
                                onChange={(e) => updateOutboundVars(prev => prev.map((item, idx) => idx === i ? { ...item, value: e.target.value } : item))}
                                placeholder="value"
                                className="flex-[2] min-w-0 font-mono text-xs bg-muted/30 border border-border/60 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-foreground"
                              />
                              <button
                                type="button"
                                onClick={() => updateOutboundVars(prev => { const n = prev.filter((_, idx) => idx !== i); return n.length === 0 ? [{ key: '', value: '' }] : n; })}
                                className="p-1 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                        {showOutboundVarsJson && (
                          <pre className="mt-2 font-mono text-[11px] bg-muted/30 border border-border/60 rounded-md p-2.5 overflow-auto max-h-24 text-muted-foreground">
                            {JSON.stringify(kvToObject(outboundVars), null, 2)}
                          </pre>
                        )}
                      </div>

                      {/* Overrides */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-foreground flex items-center gap-2">
                            <Variable className="w-4 h-4" />
                            Overrides
                            <span className="text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded font-normal">persisted</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setShowOutboundOverridesJson(p => !p)}
                              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showOutboundOverridesJson ? 'Hide JSON' : 'Show JSON'}
                            </button>
                            <button
                              type="button"
                              onClick={setOutboundExampleOverrides}
                              className="text-xs px-2 py-1 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-lg transition-all duration-300 flex items-center gap-1 font-medium"
                            >
                              <Zap className="w-3 h-3" />
                              Example
                            </button>
                            <button
                              type="button"
                              onClick={() => updateOutboundOverrideVars(prev => [...prev, { key: '', value: '' }])}
                              className="text-xs px-2 py-1 text-primary hover:text-primary/80 transition-colors font-medium flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Add
                            </button>
                          </div>
                        </div>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                          {outboundOverrideVars.map((v, i) => (
                            <div key={i} className="flex items-center gap-1.5 group">
                              <input
                                type="text"
                                value={v.key}
                                onChange={(e) => updateOutboundOverrideVars(prev => prev.map((item, idx) => idx === i ? { ...item, key: e.target.value } : item))}
                                placeholder="key"
                                className="flex-1 min-w-0 font-mono text-xs bg-muted/30 border border-border/60 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-foreground"
                              />
                              <span className="text-muted-foreground text-xs">=</span>
                              <input
                                type="text"
                                value={v.value}
                                onChange={(e) => updateOutboundOverrideVars(prev => prev.map((item, idx) => idx === i ? { ...item, value: e.target.value } : item))}
                                placeholder="value"
                                className="flex-[2] min-w-0 font-mono text-xs bg-muted/30 border border-border/60 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-foreground"
                              />
                              <button
                                type="button"
                                onClick={() => updateOutboundOverrideVars(prev => { const n = prev.filter((_, idx) => idx !== i); return n.length === 0 ? [{ key: '', value: '' }] : n; })}
                                className="p-1 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                        {showOutboundOverridesJson && (
                          <pre className="mt-2 font-mono text-[11px] bg-muted/30 border border-border/60 rounded-md p-2.5 overflow-auto max-h-24 text-muted-foreground">
                            {JSON.stringify(kvToObject(outboundOverrideVars), null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      type="submit"
                      disabled={outboundCalling || !outboundTestCall.from_number || !outboundTestCall.to_number}
                           className="w-full px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 flex items-center justify-center gap-2 font-medium"
                    >
                      {outboundCalling ? (
                        <>
                          <LoadingSpinner size="sm" />
                          Calling...
                        </>
                      ) : (
                        <>
                          <PhoneCall className="w-4 h-4" />
                          Make Outbound Call
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'knowledge-base' ? (
          <div className="flex-1 overflow-hidden min-h-0">
            {!editingAgent ? (
              <div className="flex items-center justify-center h-full p-6">
                <div className="text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Save agent first to manage knowledge base</p>
                </div>
              </div>
            ) : (
              <div className="p-6 h-full overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 h-full min-h-0">
                  {/* Left Column - Upload & Search */}
                  <div className="space-y-6 overflow-y-auto pr-2 animate-slide-in-left"
                    style={{ 
                      scrollBehavior: 'smooth',
                      maxHeight: 'calc(100vh - 320px)'
                    }}
                  >
                    {/* Upload Form */}
                    <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                          <Upload className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground">Upload Document</h3>
                      </div>
                      <form onSubmit={handleKbUpload} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            File (PDF, TXT, MD)
                          </label>
                          <input
                            type="file"
                            accept=".pdf,.txt,.md"
                            onChange={(e) => setKbSelectedFile(e.target.files[0])}
                            className="w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/20 file:text-primary hover:file:bg-primary/30 cursor-pointer"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Title (optional)
                          </label>
                          <input
                            type="text"
                            value={kbUploadTitle}
                            onChange={(e) => setKbUploadTitle(e.target.value)}
                            placeholder="Document title"
                            className="w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={kbUploading || !kbSelectedFile}
                           className="w-full px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 flex items-center justify-center gap-2 font-medium"
                        >
                          {kbUploading ? (
                            <>
                              <LoadingSpinner size="sm" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Upload Document
                            </>
                          )}
                        </button>
                      </form>
                    </div>

                    {/* Search Form */}
                    <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                          <Search className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground">Search Knowledge Base</h3>
                      </div>
                      <form onSubmit={handleKbQuery} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                            <Search className="w-4 h-4" />
                            Query
                          </label>
                          <input
                            type="text"
                            value={kbQueryText}
                            onChange={(e) => setKbQueryText(e.target.value)}
                            placeholder="What would you like to know?"
                            className="w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300"
                            required
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={kbQuerying || !kbQueryText.trim()}
                           className="w-full px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 flex items-center justify-center gap-2 font-medium"
                        >
                          {kbQuerying ? (
                            <>
                              <LoadingSpinner size="sm" />
                              Searching...
                            </>
                          ) : (
                            <>
                              <Search className="w-4 h-4" />
                              Search
                            </>
                          )}
                        </button>
                      </form>

                      {/* Search Results */}
                      {kbQueryResults.length > 0 && (
                        <div className="mt-6 space-y-4 pt-6 border-t border-border/50">
                          <div className="flex items-center gap-2 mb-4">
                            <CheckCircle className="w-5 h-5 text-success" />
                            <h4 className="text-lg font-semibold text-foreground">
                              Search Results ({kbQueryResults.length})
                            </h4>
                          </div>
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {kbQueryResults.map((result, index) => (
                              <div key={index} className="bg-background/30 border border-border/60 rounded-lg p-4">
                                <div className="flex items-center gap-3 mb-3">
                                  <span className="px-3 py-1 bg-primary/20 text-primary border border-primary/30 rounded-lg text-xs font-semibold">
                                    #{result.citation_number}
                                  </span>
                                  <span className="px-2 py-1 bg-success/20 text-success border border-success/30 rounded text-xs font-medium">
                                    {(result.score * 100).toFixed(1)}% match
                                  </span>
                                </div>
                                <p className="text-foreground text-sm mb-2 leading-relaxed">{result.text}</p>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    {result.doc_id}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" />
                                    Chunk {result.chunk_idx}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column - Documents List */}
                  <div className="flex flex-col min-h-0 overflow-hidden animate-slide-in-right animate-delay-100">
                    <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant h-full flex flex-col overflow-y-auto"
                      style={{ 
                        scrollBehavior: 'smooth',
                        maxHeight: 'calc(100vh - 320px)'
                      }}
                    >
                      <h3 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2 sticky top-0 bg-card/95 backdrop-blur-md pb-4 border-b border-border/50">
                        <FileText className="w-5 h-5" />
                        Documents ({kbDocuments.length})
                      </h3>
                      {loadingKbDocuments ? (
                        <div className="text-center py-12">
                          <LoadingSpinner size="lg" text="Loading documents..." />
                        </div>
                      ) : kbDocuments.length === 0 ? (
                        <div className="text-center py-12">
                          <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">No documents uploaded yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4 flex-1">
                          {kbDocuments.map(doc => {
                            const StatusIcon = getStatusIcon(doc.status);
                            return (
                              <div key={doc.id} className="bg-background/30 border border-border/60 rounded-lg p-4 hover:bg-background/50 transition-all duration-300">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                                      doc.status === 'completed' ? 'bg-success/20' :
                                      doc.status === 'processing' ? 'bg-warning/20' :
                                      doc.status === 'failed' ? 'bg-destructive/20' :
                                      'bg-muted/30'
                                    }`}>
                                      <StatusIcon className={`w-5 h-5 ${
                                        doc.status === 'completed' ? 'text-success' :
                                        doc.status === 'processing' ? 'text-warning' :
                                        doc.status === 'failed' ? 'text-destructive' :
                                        'text-muted-foreground'
                                      }`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-foreground font-medium mb-1 truncate">{doc.title || doc.filename}</h4>
                                      <p className="text-muted-foreground text-sm mb-2">
                                        {doc.filename} • {formatFileSize(doc.file_size)} • {doc.chunks_count} chunks
                                      </p>
                                      <span className={`text-sm font-medium ${getStatusColor(doc.status)}`}>
                                        {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                                      </span>
                                      {doc.error_message && (
                                        <span className="text-xs text-destructive flex items-center gap-1 ml-2">
                                          <AlertCircle className="w-3 h-3" />
                                          {doc.error_message}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleKbDeleteDocument(doc.id)}
                                     className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/20 hover:bg-destructive/30 text-destructive flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-destructive/20 hover:scale-110"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Delete Confirmation Modal */}
                {kbDeleteModal.show && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant max-w-md w-full mx-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-destructive/20 flex items-center justify-center">
                          <AlertCircle className="w-5 h-5 text-destructive" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">Delete Document</h3>
                      </div>
                      <p className="text-muted-foreground mb-6">
                        Are you sure you want to delete this document? This action cannot be undone.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setKbDeleteModal({ show: false, docId: null })}
                           className="flex-1 px-4 py-2 bg-background/30 border border-border/60 rounded-lg text-foreground hover:bg-background/50 transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={confirmKbDelete}
                           className="flex-1 px-4 py-2 bg-destructive text-primary-foreground rounded-lg hover:bg-destructive/90 hover:shadow-lg hover:shadow-destructive/20 hover:scale-105 transition-all duration-300"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden"
          >
            <div className={`p-6 ${activeTab === 'data-extraction' ? 'flex-1 flex flex-col min-h-0 overflow-hidden' : activeTab === 'liveavatar' ? 'space-y-6' : 'space-y-6'}`}>
              {/* LiveAvatar Tab */}
              {activeTab === 'liveavatar' && (
                <div className="space-y-6">
                  {!editingAgent ? (
                    <div className="flex items-center justify-center h-full p-6">
                      <div className="text-center">
                        <UserCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground">Save agent first to configure LiveAvatar</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                          <UserCircle className="w-5 h-5 text-primary" />
                          LiveAvatar Configuration
                        </h4>
                        <p className="text-sm text-muted-foreground mb-6">
                          Enable LiveAvatar virtual avatar for this agent. The avatar will be displayed during voice conversations.
                        </p>
                      </div>

                      {/* Enable/Disable Toggle */}
                      <div className="flex items-center gap-3 p-4 bg-muted/20 rounded-lg border border-border">
                        <AnimatedCheckbox
                          id="liveavatar_enabled"
                          checked={formData.liveavatar_enabled || false}
                          onChange={(e) => setFormData({ ...formData, liveavatar_enabled: e.target.checked })}
                          label="Enable LiveAvatar"
                          labelClassName="text-sm font-medium"
                          size="md"
                        />
                        <p className="text-xs text-muted-foreground ml-auto">
                          When enabled, a virtual avatar will be displayed during conversations
                        </p>
                      </div>

                      {/* Avatar ID Input */}
                      {formData.liveavatar_enabled && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Avatar ID *
                          </label>
                          <input
                            type="text"
                            name="liveavatar_avatar_id"
                            value={formData.liveavatar_avatar_id || ''}
                            onChange={(e) => setFormData({ ...formData, liveavatar_avatar_id: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="Enter LiveAvatar avatar ID"
                            required={formData.liveavatar_enabled}
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            Get your avatar ID from the <a href="https://app.liveavatar.com/home" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">LiveAvatar dashboard</a>
                          </p>
                        </div>
                      )}

                      {/* LiveAvatar Embed Code */}
                      {formData.liveavatar_enabled && formData.liveavatar_avatar_id && editingAgent && (
                        <div className="mt-6">
                          <label className="block text-sm font-medium text-foreground mb-2">
                            LiveAvatar Embed Code
                          </label>
                          <p className="text-xs text-muted-foreground mb-4">
                            Copy this embed code to add LiveAvatar to your website. The embed includes start and stop buttons for voice conversations.
                          </p>
                          <div className="bg-background border border-border rounded-lg p-4">
                            <div className="flex items-start gap-3 mb-3">
                              <div className="flex-1">
                                <label className="block text-xs font-medium text-muted-foreground mb-2">
                                  Embed URL
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    readOnly
                                    value={editingAgent.liveavatar_embed_url || `${window.location.origin}/embed/liveavatar/${editingAgent.id}`}
                                    className="flex-1 px-3 py-2 bg-muted/50 border border-border rounded-lg text-sm text-foreground font-mono"
                                  />
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const url = editingAgent.liveavatar_embed_url || `${window.location.origin}/embed/liveavatar/${editingAgent.id}`;
                                      await navigator.clipboard.writeText(url);
                                      setToast({ message: 'Embed URL copied to clipboard!', type: 'success' });
                                    }}
                                    className="px-3 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-2">
                                HTML Embed Code
                              </label>
                              <div className="flex gap-2">
                                <textarea
                                  readOnly
                                  value={editingAgent.liveavatar_embed_code || `<iframe src="${window.location.origin}/embed/liveavatar/${editingAgent.id}" width="100%" height="600" frameborder="0" allow="microphone; camera" style="border-radius: 16px; box-shadow: 0 12px 36px rgba(15, 23, 42, 0.18);"></iframe>`}
                                  className="flex-1 px-3 py-2 bg-muted/50 border border-border rounded-lg text-xs text-foreground font-mono h-20 resize-none"
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const code = editingAgent.liveavatar_embed_code || `<iframe src="${window.location.origin}/embed/liveavatar/${editingAgent.id}" width="100%" height="600" frameborder="0" allow="microphone; camera" style="border-radius: 16px; box-shadow: 0 12px 36px rgba(15, 23, 42, 0.18);"></iframe>`;
                                    await navigator.clipboard.writeText(code);
                                    setToast({ message: 'Embed code copied to clipboard!', type: 'success' });
                                  }}
                                  className="px-3 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-2">
                                Minimal Embed Code (Video Only)
                              </label>
                              <div className="flex gap-2">
                                <textarea
                                  readOnly
                                  value={`<iframe src="${window.location.origin}/embed/liveavatar-minimal/${editingAgent.id}" width="100%" height="600" frameborder="0" allow="microphone; camera" style="border-radius: 16px; box-shadow: 0 12px 36px rgba(15, 23, 42, 0.18);"></iframe>`}
                                  className="flex-1 px-3 py-2 bg-muted/50 border border-border rounded-lg text-xs text-foreground font-mono h-20 resize-none"
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const code = `<iframe src="${window.location.origin}/embed/liveavatar-minimal/${editingAgent.id}" width="100%" height="600" frameborder="0" allow="microphone; camera" style="border-radius: 16px; box-shadow: 0 12px 36px rgba(15, 23, 42, 0.18);"></iframe>`;
                                    await navigator.clipboard.writeText(code);
                                    setToast({ message: 'Minimal embed code copied to clipboard!', type: 'success' });
                                  }}
                                  className="px-3 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-2">
                                LiveAvatar with Product Images (Ecommerce)
                              </label>
                              <p className="text-xs text-muted-foreground mb-2">
                                Split-screen embed with avatar on the left and product images on the right. Upload images in the <strong>Product Images</strong> tab.
                              </p>
                              <div className="flex gap-2">
                                <textarea
                                  readOnly
                                  value={`<iframe src="${window.location.origin}/embed/liveavatar-products/${editingAgent.id}" width="100%" height="700" frameborder="0" allow="microphone; camera" style="border-radius: 16px; box-shadow: 0 12px 36px rgba(15, 23, 42, 0.18);"></iframe>`}
                                  className="flex-1 px-3 py-2 bg-muted/50 border border-border rounded-lg text-xs text-foreground font-mono h-20 resize-none"
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const code = `<iframe src="${window.location.origin}/embed/liveavatar-products/${editingAgent.id}" width="100%" height="700" frameborder="0" allow="microphone; camera" style="border-radius: 16px; box-shadow: 0 12px 36px rgba(15, 23, 42, 0.18);"></iframe>`;
                                    await navigator.clipboard.writeText(code);
                                    setToast({ message: 'Product embed code copied!', type: 'success' });
                                  }}
                                  className="px-3 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-2">
                                Interactive Avatar Panel (Side Drawer)
                              </label>
                              <p className="text-xs text-muted-foreground mb-2">
                                A launcher button that opens a full-height side panel with the live avatar on top and a chat transcript below. Visitors can reply by voice or by typing. Add the script tag to your site once. Optional params: <code>position=left|right</code>, <code>width</code>, <code>color</code>, <code>label</code>.
                              </p>
                              <div className="flex gap-2 mb-3">
                                <textarea
                                  readOnly
                                  value={`<script src="${window.location.origin}/avatar-panel-widget.js?agent=${editingAgent.id}"></script>`}
                                  className="flex-1 px-3 py-2 bg-muted/50 border border-border rounded-lg text-xs text-foreground font-mono h-16 resize-none"
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const code = `<script src="${window.location.origin}/avatar-panel-widget.js?agent=${editingAgent.id}"></script>`;
                                    await navigator.clipboard.writeText(code);
                                    setToast({ message: 'Avatar panel snippet copied!', type: 'success' });
                                  }}
                                  className="px-3 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <label className="block text-xs font-medium text-muted-foreground mb-2">
                                Direct Panel URL
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  readOnly
                                  value={`${window.location.origin}/embed/avatar-panel/${editingAgent.id}`}
                                  className="flex-1 px-3 py-2 bg-muted/50 border border-border rounded-lg text-sm text-foreground font-mono"
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const url = `${window.location.origin}/embed/avatar-panel/${editingAgent.id}`;
                                    await navigator.clipboard.writeText(url);
                                    setToast({ message: 'Panel URL copied to clipboard!', type: 'success' });
                                  }}
                                  className="px-3 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="border border-primary/25 bg-primary/5 rounded-xl p-4">
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div>
                                  <label className="block text-xs font-semibold text-foreground">
                                    Interactive Avatar Panel V2 &mdash; drawer or spotlight
                                  </label>
                                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                    The redesigned panel. One snippet, two placements: a full-height side drawer, or a bottom
                                    command pill that grows into a centered card. Neither dims or blurs the page, so visitors
                                    keep browsing while they talk, and minimizing collapses it to a pill without ending the
                                    conversation. Colors, copy, launcher and placement are set in the customizer.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setShowAvatarPanelCustomizer(true)}
                                  className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-xs font-semibold flex-shrink-0"
                                >
                                  Customize
                                </button>
                              </div>
                              <div className="flex gap-2">
                                <textarea
                                  readOnly
                                  value={`<script src="${window.location.origin}/avatar-panel-v2.js?agent=${editingAgent.id}" async></script>`}
                                  className="flex-1 px-3 py-2 bg-muted/50 border border-border rounded-lg text-xs text-foreground font-mono h-16 resize-none"
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const code = `<script src="${window.location.origin}/avatar-panel-v2.js?agent=${editingAgent.id}" async></script>`;
                                    await navigator.clipboard.writeText(code);
                                    setToast({ message: 'Avatar panel V2 snippet copied!', type: 'success' });
                                  }}
                                  className="px-3 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => window.open(`${window.location.origin}/avatar-panel-v2-demo.html?agent=${editingAgent.id}`, '_blank')}
                                className="mt-2 text-xs text-primary hover:underline"
                              >
                                Open it on a demo page &rarr;
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Product Images Tab */}
              {activeTab === 'product-images' && (
                <ProductImagesTab editingAgent={editingAgent} />
              )}

              {/* Default Variables Tab */}
              {activeTab === 'default-variables' && (
                <DefaultVariablesTab />
              )}

              {/* Basic & AI Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Agent Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Enter agent name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Agent Type
                      </label>
                      <AnimatedDropdown
                        value={formData.agent_type || 'prompt'}
                        onChange={(value) => {
                          const updates = { agent_type: value };
                          // Clear flow-specific fields when switching to prompt
                          if (value === 'prompt') {
                            updates.conversation_flow = null;
                            updates.global_prompt = '';
                            updates.required_variables = [];
                          }
                          // Clear prompt-specific fields when switching to flow
                          if (value === 'flow') {
                            updates.system_prompt = '';
                          }
                          setFormData({ ...formData, ...updates });
                        }}
                        options={[
                          { value: 'prompt', label: 'Prompt-based' },
                          { value: 'flow', label: 'Flow-based' }
                        ]}
                        placeholder="Select agent type"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        AI Model
                      </label>
                      <AnimatedDropdown
                        value={formData.llm_model}
                        onChange={(value) => setFormData({ ...formData, llm_model: value })}
                        options={[
                          // GPT-5 and newer ignore the Temperature setting below:
                          // OpenAI only accepts the model default on those models.
                          { value: 'gpt-6-astra', label: 'GPT-6 Astra (flagship)' },
                          { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol (flagship)' },
                          { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra (balanced)' },
                          { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna (fastest, best for voice)' },
                          { value: 'gpt-5.5', label: 'GPT-5.5' },
                          { value: 'gpt-5.4', label: 'GPT-5.4' },
                          { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini' },
                          { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano' },
                          { value: 'gpt-5.2', label: 'GPT-5.2' },
                          { value: 'gpt-5.1', label: 'GPT-5.1' },
                          { value: 'gpt-5', label: 'GPT-5' },
                          { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
                          { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
                          { value: 'gpt-4.1', label: 'GPT-4.1' },
                          { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
                          { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
                          { value: 'gpt-4o', label: 'GPT-4o' },
                          { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
                          { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
                          { value: 'gpt-4', label: 'GPT-4' },
                          { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (legacy)' },
                          // Claude Opus 5, Sonnet 5 and Opus 4.8 ignore the
                          // Temperature setting below, same as GPT-5 and newer.
                          { value: 'claude-opus-5', label: 'Claude Opus 5 (flagship)' },
                          { value: 'claude-sonnet-5', label: 'Claude Sonnet 5 (balanced)' },
                          { value: 'claude-opus-4.8', label: 'Claude Opus 4.8' },
                          { value: 'claude-opus-4.5', label: 'Claude Opus 4.5' },
                          { value: 'claude-sonnet-4.5', label: 'Claude Sonnet 4.5' },
                          { value: 'claude-haiku-4.5', label: 'Claude Haiku 4.5 (fastest)' },
                          { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash (flagship)' },
                          { value: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash' },
                          { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
                          { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash (balanced)' },
                          { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite (fastest, best for voice)' },
                          { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (preview)' },
                          { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' }
                        ]}
                        placeholder="Select AI model"
                      />
                    </div>
                  </div>

                  {/* Temperature and Language Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Temperature */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-foreground">Temperature</label>
                        <span className="text-sm text-muted-foreground">{formData.temperature}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={formData.temperature}
                        onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>Conservative</span>
                        <span>Balanced</span>
                        <span>Creative</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Controls randomness. Higher temperature for creative tasks; lower for accuracy.
                      </p>
                    </div>

                    {/* Language */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Language
                      </label>
                      <AnimatedDropdown
                        value={formData.locale}
                        onChange={(value) => setFormData({ ...formData, locale: value })}
                        options={[
                          { value: 'en-US', label: 'English (US)' },
                          { value: 'en-GB', label: 'English (UK)' },
                          { value: 'es-ES', label: 'Spanish' },
                          { value: 'fr-FR', label: 'French' },
                          { value: 'de-DE', label: 'German' }
                        ]}
                        placeholder="Select language"
                      />
                    </div>
                  </div>

                  {/* System Prompt - Only show for prompt-based agents */}
                  {formData.agent_type === 'prompt' && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <label className="text-sm font-medium text-foreground">System Prompt</label>
                          <p className="text-xs text-muted-foreground">Define persona, goals, rules</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                          className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10 px-2 py-1 rounded-lg hover:bg-primary/10"
                        >
                          <Maximize2 className={`w-4 h-4 transition-transform duration-300 ${isPromptExpanded ? 'rotate-180' : ''}`} />
                          {isPromptExpanded ? 'Collapse' : 'Expand'} Prompt
                        </button>
                      </div>
                      <textarea
                        value={formData.system_prompt}
                        onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                        required
                        rows={isPromptExpanded ? 20 : 8}
                        className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm resize-none"
                        placeholder="You are a helpful AI assistant..."
                      />
                    </div>
                  )}

                  {/* Chat System Prompt Override - Only show for prompt-based agents */}
                  {formData.agent_type === 'prompt' && (
                    <div className="mt-6">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                          <label className="text-sm font-medium text-foreground">Chat System Prompt (optional)</label>
                          <p className="text-xs text-muted-foreground">
                            Overrides the System Prompt above for chat only (widget and chat API). Voice calls always use the System Prompt above. Leave blank to use the same prompt for both.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsChatPromptExpanded(!isChatPromptExpanded)}
                          className="flex-shrink-0 text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10 px-2 py-1 rounded-lg hover:bg-primary/10 whitespace-nowrap"
                        >
                          <Maximize2 className={`w-4 h-4 transition-transform duration-300 ${isChatPromptExpanded ? 'rotate-180' : ''}`} />
                          {isChatPromptExpanded ? 'Collapse' : 'Expand'} Prompt
                        </button>
                      </div>
                      <textarea
                        value={formData.chat_system_prompt}
                        onChange={(e) => setFormData({ ...formData, chat_system_prompt: e.target.value })}
                        rows={isChatPromptExpanded ? 20 : 6}
                        className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm resize-none"
                        placeholder="Leave blank to reuse the System Prompt above for chat..."
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Conversation Flow Tab */}
              {activeTab === 'flow' && (
                <div className="space-y-6">
                  {formData.agent_type !== 'flow' ? (
                    <div className="p-6 bg-muted/50 border border-border rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        Please set Agent Type to "Flow-based" in the Basic & AI tab to use Conversation Flow.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Global Prompt */}
                      <div>
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Global Prompt
                            </label>
                            <p className="text-xs text-muted-foreground">
                              This prompt will be included at the beginning of the system prompt for all nodes in the flow.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsGlobalPromptExpanded(!isGlobalPromptExpanded)}
                            className="flex-shrink-0 text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10 px-2 py-1 rounded-lg hover:bg-primary/10 whitespace-nowrap"
                          >
                            <Maximize2 className={`w-4 h-4 transition-transform duration-300 ${isGlobalPromptExpanded ? 'rotate-180' : ''}`} />
                            {isGlobalPromptExpanded ? 'Collapse' : 'Expand'} Prompt
                          </button>
                        </div>
                        <textarea
                          value={formData.global_prompt || ''}
                          // Functional update: never spread a stale formData over this keystroke
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, global_prompt: value }));
                          }}
                          rows={isGlobalPromptExpanded ? 20 : 4}
                          className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm resize-none"
                          placeholder="You are a helpful assistant that can answer questions and help with tasks."
                        />
                      </div>

                      {/* Flow Builder */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-foreground">Flow Canvas</label>
                          <button
                            type="button"
                            onClick={() => {
                              const key = 'flowEditorMaximizedData';
                              sessionStorage.setItem(key, JSON.stringify({
                                flowData: formData.conversation_flow,
                                requiredVariables: formData.required_variables || [],
                              }));
                              const url = `${window.location.origin}/flow-editor-maximized`;
                              window.open(url, 'flowEditorMaximized', 'width=1400,height=900,menubar=no,toolbar=no,location=no,status=no');
                            }}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-foreground text-sm"
                            title="Open flow editor in a larger window for better editing experience"
                          >
                            <Maximize2 size={18} />
                            Maximize
                          </button>
                        </div>
                        <div className="border border-border rounded-lg overflow-hidden" style={{ height: '600px' }}>
                          <FlowBuilder
                            ref={flowBuilderRef}
                            flowData={formData.conversation_flow}
                            // Functional update: a flow sync must not overwrite fields edited meanwhile (e.g. Global Prompt)
                            onFlowChange={(flow) => setFormData((prev) => ({ ...prev, conversation_flow: flow }))}
                            requiredVariables={formData.required_variables || []}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Voice Tab */}
              {activeTab === 'voice' && (
                <div className="space-y-4">
                  {/* Voice Provider and ElevenLabs Model Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Voice Provider
                      </label>
                      <AnimatedDropdown
                        value={formData.voice_provider}
                        onChange={(value) => setFormData({ ...formData, voice_provider: value })}
                        options={[
                          { value: 'elevenlabs', label: 'ElevenLabs' },
                          { value: 'cartesia', label: 'Cartesia' },
                          { value: 'sonic-3', label: 'Cartesia Sonic' },
                          { value: 'fishaudio', label: 'Fish Audio' }
                        ]}
                        placeholder="Select voice provider"
                      />
                    </div>
                    {formData.voice_provider === 'elevenlabs' && (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          ElevenLabs Model
                        </label>
                        <AnimatedDropdown
                          value={formData.elevenlabs_model || 'eleven_turbo_v2_5'}
                          onChange={(value) => setFormData({ ...formData, elevenlabs_model: value })}
                          options={[
                            { value: 'eleven_turbo_v2_5', label: 'Turbo v2.5 (Ultra-fast, supports speed)' },
                            { value: 'eleven_flash_v2_5', label: 'Flash v2.5 (Ultra-fast, supports speed)' },
                            { value: 'eleven_multilingual_v2', label: 'Multilingual v2 (70+ languages)' }
                          ]}
                          placeholder="Select model"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {formData.elevenlabs_model === 'eleven_multilingual_v2'
                            ? 'Supports 70+ languages. Language is auto-detected from text.'
                            : formData.elevenlabs_model === 'eleven_flash_v2_5'
                            ? 'Ultra-fast model optimized for real-time use. Supports speed parameter.'
                            : 'Ultra-fast model optimized for real-time use. Supports speed parameter.'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* STT Provider Selection */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Speech-to-Text Provider
                    </label>
                    <AnimatedDropdown
                      value={formData.stt_provider || 'deepgram'}
                      onChange={(value) => {
                        // Reset provider-specific fields when switching providers
                        if (value === 'gladia') {
                          // Initialize Gladia defaults when switching to Gladia
                          setFormData({ 
                            ...formData, 
                            stt_provider: value,
                            gladia_languages: '["en"]',
                            gladia_translation_enabled: true,
                            gladia_translation_target_languages: '["es"]',
                            gladia_endpointing: formData.gladia_endpointing ?? 0.05,
                            gladia_speech_threshold: formData.gladia_speech_threshold ?? 0.6,
                            // Reset Soniox fields
                            soniox_model: null,
                            soniox_language_hints: null,
                            soniox_language_hints_strict: false,
                            soniox_enable_language_identification: true,
                            soniox_translation_enabled: false,
                            soniox_translation_source_languages: null,
                            soniox_translation_target_languages: null,
                            soniox_translation_terms: null,
                            soniox_context: null
                          });
                        } else if (value === 'soniox') {
                          // Initialize Soniox defaults when switching to Soniox
                          setFormData({ 
                            ...formData, 
                            stt_provider: value,
                            soniox_model: formData.soniox_model || 'stt-rt-v3',
                            soniox_language_hints: formData.soniox_language_hints || '["en"]',
                            soniox_language_hints_strict: formData.soniox_language_hints_strict ?? false,
                            soniox_enable_language_identification: formData.soniox_enable_language_identification ?? true,
                            soniox_translation_enabled: formData.soniox_translation_enabled ?? false,
                            soniox_translation_source_languages: formData.soniox_translation_source_languages || '["en"]',
                            soniox_translation_target_languages: formData.soniox_translation_target_languages || '["es"]',
                            soniox_translation_terms: formData.soniox_translation_terms || null,
                            soniox_context: formData.soniox_context || null,
                            // Reset Gladia fields
                            gladia_languages: null,
                            gladia_translation_enabled: false,
                            gladia_translation_target_languages: null,
                            gladia_endpointing: null,
                            gladia_speech_threshold: null
                          });
                        } else {
                          // Reset all provider-specific fields when switching to Deepgram or other
                          setFormData({ 
                            ...formData, 
                            stt_provider: value,
                            gladia_languages: null,
                            gladia_translation_enabled: false,
                            gladia_translation_target_languages: null,
                            gladia_endpointing: null,
                            gladia_speech_threshold: null,
                            soniox_model: null,
                            soniox_language_hints: null,
                            soniox_language_hints_strict: false,
                            soniox_enable_language_identification: true,
                            soniox_translation_enabled: false,
                            soniox_translation_source_languages: null,
                            soniox_translation_target_languages: null,
                            soniox_translation_terms: null,
                            soniox_context: null
                          });
                        }
                      }}
                      options={[
                        { value: 'deepgram', label: 'Deepgram' },
                        { value: 'gladia', label: 'Gladia' },
                        { value: 'soniox', label: 'Soniox' }
                      ]}
                      placeholder="Select STT provider"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Choose your speech-to-text provider
                    </p>
                  </div>

                  {/* Deepgram STT Configuration */}
                  {(!formData.stt_provider || formData.stt_provider === 'deepgram') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Deepgram Model
                        </label>
                        <AnimatedDropdown
                          value={formData.stt_model || 'nova3_mono_en'}
                          onChange={(value) => {
                            // When switching to multilingual, ALWAYS set language to 'multi'
                            // When switching to mono, reset language to 'en-US' if it was 'multi'
                            if (value === 'nova3_multi') {
                              setFormData({ ...formData, stt_model: value, stt_language: 'multi' });
                            } else if (value === 'nova3_mono_en' && formData.stt_language === 'multi') {
                              setFormData({ ...formData, stt_model: value, stt_language: 'en-US' });
                            } else {
                              setFormData({ ...formData, stt_model: value });
                            }
                          }}
                          options={[
                            { value: 'nova3_mono_en', label: 'Nova-3 Mono' },
                            { value: 'nova3_multi', label: 'Nova-3 Multilingual (Auto-detect)' }
                          ]}
                          placeholder="Select STT model"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {formData.stt_model === 'nova3_multi' 
                            ? 'Automatically detects multiple languages'
                            : 'Single language mode'}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          STT Language
                        </label>
                        <AnimatedDropdown
                          value={formData.stt_model === 'nova3_multi' ? 'multi' : (formData.stt_language || 'en-US')}
                          onChange={(value) => setFormData({ ...formData, stt_language: value })}
                          disabled={formData.stt_model === 'nova3_multi'}
                          options={[
                            { value: 'multi', label: 'Multi (Auto-detect)' },
                            { value: 'en-US', label: 'English (US)' },
                            { value: 'en', label: 'English' },
                            { value: 'en-AU', label: 'English (Australia)' },
                            { value: 'en-GB', label: 'English (UK)' },
                            { value: 'en-IN', label: 'English (India)' },
                            { value: 'en-NZ', label: 'English (New Zealand)' },
                            { value: 'es', label: 'Spanish' },
                            { value: 'es-419', label: 'Spanish (Latin America)' },
                            { value: 'fr', label: 'French' },
                            { value: 'fr-CA', label: 'French (Canada)' },
                            { value: 'de', label: 'German' },
                            { value: 'de-CH', label: 'German (Switzerland)' },
                            { value: 'pt', label: 'Portuguese' },
                            { value: 'pt-BR', label: 'Portuguese (Brazil)' },
                            { value: 'pt-PT', label: 'Portuguese (Portugal)' },
                            { value: 'it', label: 'Italian' },
                            { value: 'ja', label: 'Japanese' },
                            { value: 'ko', label: 'Korean' },
                            { value: 'ko-KR', label: 'Korean (South Korea)' },
                            { value: 'hi', label: 'Hindi' },
                            { value: 'ru', label: 'Russian' },
                            { value: 'nl', label: 'Dutch' },
                            { value: 'nl-BE', label: 'Flemish' },
                            { value: 'bg', label: 'Bulgarian' },
                            { value: 'ca', label: 'Catalan' },
                            { value: 'cs', label: 'Czech' },
                            { value: 'da', label: 'Danish' },
                            { value: 'da-DK', label: 'Danish (Denmark)' },
                            { value: 'et', label: 'Estonian' },
                            { value: 'fi', label: 'Finnish' },
                            { value: 'el', label: 'Greek' },
                            { value: 'hu', label: 'Hungarian' },
                            { value: 'id', label: 'Indonesian' },
                            { value: 'lv', label: 'Latvian' },
                            { value: 'lt', label: 'Lithuanian' },
                            { value: 'ms', label: 'Malay' },
                            { value: 'no', label: 'Norwegian' },
                            { value: 'pl', label: 'Polish' },
                            { value: 'ro', label: 'Romanian' },
                            { value: 'sk', label: 'Slovak' },
                            { value: 'sv', label: 'Swedish' },
                            { value: 'sv-SE', label: 'Swedish (Sweden)' },
                            { value: 'tr', label: 'Turkish' },
                            { value: 'uk', label: 'Ukrainian' },
                            { value: 'vi', label: 'Vietnamese' }
                          ]}
                          placeholder="Select language"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {formData.stt_model === 'nova3_multi'
                            ? 'Auto-detects all supported languages'
                            : `Transcribes audio in ${formData.stt_language || 'en-US'}`}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Gladia STT Configuration */}
                  {formData.stt_provider === 'gladia' && (
                    <div className="space-y-4">
                      {/* Languages Selection */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Languages
                        </label>
                        <MultiSelect
                          value={(() => {
                            try {
                              const langs = JSON.parse(formData.gladia_languages || '["en"]');
                              return Array.isArray(langs) ? langs : ['en'];
                            } catch {
                              return ['en'];
                            }
                          })()}
                          onChange={(selected) => {
                            setFormData({ ...formData, gladia_languages: JSON.stringify(selected.length > 0 ? selected : ['en']) });
                          }}
                          options={GLADIA_LANGUAGE_OPTIONS}
                          placeholder="Select languages for transcription"
                          searchable={true}
                          showSelectedCount={true}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Select one or more languages for transcription. If empty, Gladia will auto-detect.
                        </p>
                      </div>

                      {/* Translation Enabled Toggle */}
                      <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Translation Enabled
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Enable real-time translation of transcriptions
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newValue = !formData.gladia_translation_enabled;
                            setFormData({ 
                              ...formData, 
                              gladia_translation_enabled: newValue,
                              // Reset target languages if disabling translation
                              gladia_translation_target_languages: newValue ? (formData.gladia_translation_target_languages || '["es"]') : null
                            });
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.gladia_translation_enabled ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.gladia_translation_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Translation Target Languages */}
                      {formData.gladia_translation_enabled && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Translation Target Languages
                          </label>
                          <MultiSelect
                            value={(() => {
                              try {
                                const langs = JSON.parse(formData.gladia_translation_target_languages || '["es"]');
                                return Array.isArray(langs) ? langs : ['es'];
                              } catch {
                                return ['es'];
                              }
                            })()}
                            onChange={(selected) => {
                              setFormData({ ...formData, gladia_translation_target_languages: JSON.stringify(selected.length > 0 ? selected : ['es']) });
                            }}
                            options={GLADIA_LANGUAGE_OPTIONS}
                            placeholder="Select target languages for translation"
                            searchable={true}
                            showSelectedCount={true}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select one or more target languages for translation.
                          </p>
                        </div>
                      )}

                      {/* Endpointing Slider */}
                      <div className="p-4 bg-muted/20 border border-border rounded-lg">
                        <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                          <span>Endpointing</span>
                          <span>{Number(formData.gladia_endpointing ?? 0.05).toFixed(2)}s</span>
                        </div>
                        <input
                          type="range"
                          min="0.01"
                          max="10"
                          step="0.01"
                          value={Number(formData.gladia_endpointing ?? 0.05)}
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              gladia_endpointing: parseFloat(event.target.value),
                            })
                          }
                          className="w-full accent-primary"
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Duration of silence (in seconds) that marks the end of an utterance. Lower values = faster response, higher values = wait longer for speech.
                        </p>
                      </div>

                      {/* Speech Threshold Slider */}
                      <div className="p-4 bg-muted/20 border border-border rounded-lg">
                        <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                          <span>Speech Threshold</span>
                          <span>{Number(formData.gladia_speech_threshold ?? 0.6).toFixed(2)}</span>
                        </div>
                        <input
                          type="range"
                          min="0.1"
                          max="0.9"
                          step="0.01"
                          value={Number(formData.gladia_speech_threshold ?? 0.6)}
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              gladia_speech_threshold: parseFloat(event.target.value),
                            })
                          }
                          className="w-full accent-primary"
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Sensitivity for speech detection. Higher values filter out more background noise (recommended: 0.6 for normal audio, 0.7 for poor-quality audio).
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Soniox STT Configuration */}
                  {formData.stt_provider === 'soniox' && (
                    <div className="space-y-4">
                      {/* Model Selection */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Soniox Model
                        </label>
                        <AnimatedDropdown
                          value={formData.soniox_model || 'stt-rt-v3'}
                          onChange={(value) => setFormData({ ...formData, soniox_model: value })}
                          options={[
                            { value: 'stt-rt-v3', label: 'STT Real-Time v3 (Default)' }
                          ]}
                          placeholder="Select Soniox model"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Soniox STT model for real-time transcription
                        </p>
                      </div>

                      {/* Language Hints */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Language Hints
                        </label>
                        <MultiSelect
                          value={(() => {
                            try {
                              const langs = JSON.parse(formData.soniox_language_hints || '["en"]');
                              return Array.isArray(langs) ? langs : ['en'];
                            } catch {
                              return ['en'];
                            }
                          })()}
                          onChange={(selected) => {
                            setFormData({ ...formData, soniox_language_hints: JSON.stringify(selected.length > 0 ? selected : ['en']) });
                          }}
                          options={SONIOX_LANGUAGE_OPTIONS}
                          placeholder="Select language hints for transcription"
                          searchable={true}
                          showSelectedCount={true}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Provide language hints to improve transcription accuracy. Soniox will auto-detect if not specified.
                        </p>
                      </div>

                      {/* Language Hints Strict Mode */}
                      <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Language Hints Strict Mode
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Only transcribe in the specified language hints
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, soniox_language_hints_strict: !formData.soniox_language_hints_strict });
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.soniox_language_hints_strict ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.soniox_language_hints_strict ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Enable Language Identification */}
                      <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Enable Language Identification
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Automatically identify the language of input audio
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, soniox_enable_language_identification: !formData.soniox_enable_language_identification });
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.soniox_enable_language_identification !== false ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.soniox_enable_language_identification !== false ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Two-Way Translation Enabled Toggle */}
                      <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Two-Way Translation Enabled
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Enable real-time translation between source and target languages
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newValue = !formData.soniox_translation_enabled;
                            setFormData({ 
                              ...formData, 
                              soniox_translation_enabled: newValue,
                              // Initialize default languages if enabling translation
                              soniox_translation_source_languages: newValue ? (formData.soniox_translation_source_languages || '["en"]') : null,
                              soniox_translation_target_languages: newValue ? (formData.soniox_translation_target_languages || '["es"]') : null
                            });
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.soniox_translation_enabled ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.soniox_translation_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Translation Source Languages */}
                      {formData.soniox_translation_enabled && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Translation Source Languages
                          </label>
                          <MultiSelect
                            value={(() => {
                              try {
                                const langs = JSON.parse(formData.soniox_translation_source_languages || '["en"]');
                                return Array.isArray(langs) ? langs : ['en'];
                              } catch {
                                return ['en'];
                              }
                            })()}
                            onChange={(selected) => {
                              setFormData({ ...formData, soniox_translation_source_languages: JSON.stringify(selected.length > 0 ? selected : ['en']) });
                            }}
                            options={SONIOX_LANGUAGE_OPTIONS}
                            placeholder="Select source languages for translation"
                            searchable={true}
                            showSelectedCount={true}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Languages to transcribe from (e.g., English, Spanish)
                          </p>
                        </div>
                      )}

                      {/* Translation Target Languages */}
                      {formData.soniox_translation_enabled && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Translation Target Languages
                          </label>
                          <MultiSelect
                            value={(() => {
                              try {
                                const langs = JSON.parse(formData.soniox_translation_target_languages || '["es"]');
                                return Array.isArray(langs) ? langs : ['es'];
                              } catch {
                                return ['es'];
                              }
                            })()}
                            onChange={(selected) => {
                              setFormData({ ...formData, soniox_translation_target_languages: JSON.stringify(selected.length > 0 ? selected : ['es']) });
                            }}
                            options={SONIOX_LANGUAGE_OPTIONS}
                            placeholder="Select target languages for translation"
                            searchable={true}
                            showSelectedCount={true}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Languages to translate to (e.g., Spanish, English). Soniox supports translation between any pair of supported languages.
                          </p>
                        </div>
                      )}

                      {/* Translation Terms (Optional) */}
                      {formData.soniox_translation_enabled && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Custom Translation Terms (Optional)
                          </label>
                          <textarea
                            value={formData.soniox_translation_terms || ''}
                            onChange={(e) => setFormData({ ...formData, soniox_translation_terms: e.target.value })}
                            placeholder='[{"source": "hello", "target": "hola"}, {"source": "goodbye", "target": "adiós"}]'
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            rows={4}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            JSON array of custom translation term mappings. Format: {'[{'}source: "term1", target: "translation1"{'}]'}
                          </p>
                        </div>
                      )}

                      {/* Context Text (Optional) */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Domain-Specific Context (Optional)
                        </label>
                        <textarea
                          value={formData.soniox_context || ''}
                          onChange={(e) => setFormData({ ...formData, soniox_context: e.target.value })}
                          placeholder="Provide domain-specific context or vocabulary to improve transcription accuracy..."
                          className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Free-form text that provides additional context or vocabulary to bias transcription towards domain-specific terms.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Voice ID Fields */}
                  {formData.voice_provider === 'elevenlabs' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        ElevenLabs Voice ID
                      </label>
                      <input
                        type="text"
                        value={formData.elevenlabs_voice_id}
                        onChange={(e) => setFormData({ ...formData, elevenlabs_voice_id: e.target.value })}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="21m00Tcm4TlvDq8ikWAM"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter your ElevenLabs voice ID
                      </p>
                    </div>
                  )}

                  {formData.voice_provider === 'cartesia' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Cartesia Voice ID
                      </label>
                      <input
                        type="text"
                        value={formData.cartesia_voice_id}
                        onChange={(e) => setFormData({ ...formData, cartesia_voice_id: e.target.value })}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Enter Cartesia voice ID"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Enter your Cartesia voice ID
                      </p>
                    </div>
                  )}

                  {formData.voice_provider === 'sonic-3' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Sonic Model
                      </label>
                      <AnimatedDropdown
                        value={formData.sonic_model || 'sonic-3'}
                        onChange={(value) => setFormData({ ...formData, sonic_model: value })}
                        options={[
                          { value: 'sonic-3', label: 'Sonic 3' },
                          { value: 'sonic-3.6', label: 'Sonic 3.6 (latest)' }
                        ]}
                        placeholder="Select Sonic model"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Sonic 3.6 is Cartesia's newest model, with more natural delivery and
                        broader language coverage. It uses the same voice IDs as Sonic 3, so
                        you can switch without changing the voice. It sets its own pacing and
                        reads emotion from the script, so the speed and emotion controls below
                        apply to Sonic 3 only. Volume applies to both.
                      </p>
                    </div>
                  )}

                  {(formData.voice_provider === 'sonic-3' || formData.voice_provider === 'cartesia') && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Cartesia Voice Language
                      </label>
                      <AnimatedDropdown
                        value={formData.cartesia_language || ''}
                        onChange={(value) => setFormData({ ...formData, cartesia_language: value })}
                        options={cartesiaLanguageOptions}
                        placeholder="Follow agent Language setting"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Sets the language Cartesia speaks, separately from the agent Language
                        setting above. Leave it on the default to follow that setting. The list
                        shows only what the selected model speaks, so Urdu and Odia appear on
                        Sonic 3.6 only.
                      </p>
                    </div>
                  )}

                  {formData.voice_provider === 'sonic-3' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Sonic Voice ID
                      </label>
                      <input
                        type="text"
                        value={formData.sonic_voice_id || ''}
                        onChange={(e) => setFormData({ ...formData, sonic_voice_id: e.target.value })}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Enter Sonic voice ID"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Cartesia voice ID. The same voice IDs work on Sonic 3 and Sonic 3.6.
                      </p>
                    </div>
                  )}

                  {formData.voice_provider === 'sonic-3' && (
                    <div className="p-4 border border-border rounded-lg bg-muted/10 space-y-4">
                      <div>
                        <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                          <span>Sonic Speed</span>
                          <span>{Number(formData.sonic_speed ?? 1).toFixed(2)}x</span>
                        </div>
                        <input
                          type="range"
                          min="0.6"
                          max="1.5"
                          step="0.05"
                          disabled={isSonic36}
                          value={Number(formData.sonic_speed ?? 1)}
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              sonic_speed: parseFloat(event.target.value),
                            })
                          }
                          className={`w-full accent-primary ${isSonic36 ? 'opacity-40 cursor-not-allowed' : ''}`}
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          {isSonic36
                            ? 'Sonic 3.6 sets its own pacing, so this slider does not change the voice. Your saved value is kept for Sonic 3.'
                            : 'Guides Sonic-3 speaking speed (0.6x = slower, 1.5x = faster).'}
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                          <span>Sonic Volume</span>
                          <span>{Number(formData.sonic_volume ?? 1).toFixed(2)}x</span>
                        </div>
                        <input
                          type="range"
                          min="0.5"
                          max="2.0"
                          step="0.05"
                          value={Number(formData.sonic_volume ?? 1)}
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              sonic_volume: parseFloat(event.target.value),
                            })
                          }
                          className="w-full accent-primary"
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Guides Sonic loudness (0.5x = softer, 2.0x = louder). Applies to both models.
                        </p>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground mb-2 block">
                          Sonic Emotion (Beta)
                        </label>
                        <AnimatedDropdown
                          value={formData.sonic_emotion || 'neutral'}
                          onChange={(value) => setFormData({ ...formData, sonic_emotion: value })}
                          options={sonicEmotionOptions}
                          placeholder="Select emotion"
                          disabled={isSonic36}
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          {isSonic36
                            ? 'Sonic 3.6 reads emotion from the script itself, so this tag does not steer it. Your saved value is kept for Sonic 3.'
                            : 'Emotion tags steer delivery but must match the script tone for best results.'}
                        </p>
                      </div>
                    </div>
                  )}


                  {formData.voice_provider === 'fishaudio' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Fish Audio Voice ID
                        </label>
                        <input
                          type="text"
                          value={formData.fishaudio_voice_id || ''}
                          onChange={(e) => setFormData({ ...formData, fishaudio_voice_id: e.target.value })}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="933563129e564b19a115bedd57b7406a"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          Fish Audio reference ID from the voice library or your own cloned voice.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Fish Audio Model
                        </label>
                        <AnimatedDropdown
                          value={formData.fishaudio_model || 's2.1-pro'}
                          onChange={(value) => setFormData({ ...formData, fishaudio_model: value })}
                          options={fishaudioModelOptions}
                          placeholder="Select model"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          S2.1 Pro is the production model. Expressive mode needs an S2-Pro family model.
                        </p>
                      </div>
                    </div>
                  )}

                  {formData.voice_provider === 'fishaudio' && (
                    <div className="p-4 border border-border rounded-lg bg-muted/10 space-y-4">
                      <div className="text-xs font-semibold text-foreground uppercase tracking-wide">
                        Fish Audio Voice Controls
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground mb-2 block">
                          Latency Mode
                        </label>
                        <AnimatedDropdown
                          value={formData.fishaudio_latency_mode || 'balanced'}
                          onChange={(value) => setFormData({ ...formData, fishaudio_latency_mode: value })}
                          options={fishaudioLatencyOptions}
                          placeholder="Select latency mode"
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Trade-off between how fast the first audio arrives and synthesis quality.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                            <span>Speed</span>
                            <span>{Number(formData.fishaudio_speed ?? 1).toFixed(2)}x</span>
                          </div>
                          <input
                            type="range"
                            min="0.5"
                            max="2"
                            step="0.05"
                            value={Number(formData.fishaudio_speed ?? 1)}
                            onChange={(event) =>
                              setFormData({ ...formData, fishaudio_speed: parseFloat(event.target.value) })
                            }
                            className="w-full accent-primary"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Speaking rate. 1.00x is the natural pace of the voice.
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                            <span>Volume</span>
                            <span>{Number(formData.fishaudio_volume ?? 0).toFixed(1)} dB</span>
                          </div>
                          <input
                            type="range"
                            min="-20"
                            max="20"
                            step="0.5"
                            value={Number(formData.fishaudio_volume ?? 0)}
                            onChange={(event) =>
                              setFormData({ ...formData, fishaudio_volume: parseFloat(event.target.value) })
                            }
                            className="w-full accent-primary"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Loudness adjustment in decibels. 0 dB leaves the level unchanged.
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                            <span>Temperature</span>
                            <span>{Number(formData.fishaudio_temperature ?? 0.7).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={Number(formData.fishaudio_temperature ?? 0.7)}
                            onChange={(event) =>
                              setFormData({ ...formData, fishaudio_temperature: parseFloat(event.target.value) })
                            }
                            className="w-full accent-primary"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Higher values sound more varied and expressive, lower values more stable.
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                            <span>Top P</span>
                            <span>{Number(formData.fishaudio_top_p ?? 0.7).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={Number(formData.fishaudio_top_p ?? 0.7)}
                            onChange={(event) =>
                              setFormData({ ...formData, fishaudio_top_p: parseFloat(event.target.value) })
                            }
                            className="w-full accent-primary"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Nucleus sampling. Lower keeps delivery predictable, higher allows more variety.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs font-semibold text-foreground mb-2 block">
                            Chunk Length (100-300)
                          </label>
                          <input
                            type="number"
                            min="100"
                            max="300"
                            step="10"
                            value={formData.fishaudio_chunk_length ?? 100}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                fishaudio_chunk_length: e.target.value === '' ? null : parseInt(e.target.value, 10),
                              })
                            }
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Max characters synthesized per chunk. Lower means smaller, more frequent audio bursts.
                          </p>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-foreground mb-2 block">
                            Min Chunk Length (0-100)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="5"
                            value={formData.fishaudio_min_chunk_length ?? 50}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                fishaudio_min_chunk_length: e.target.value === '' ? null : parseInt(e.target.value, 10),
                              })
                            }
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Minimum characters before a new chunk starts.
                          </p>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-foreground mb-2 block">
                            Max New Tokens
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="128"
                            value={formData.fishaudio_max_new_tokens ?? 1024}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                fishaudio_max_new_tokens: e.target.value === '' ? null : parseInt(e.target.value, 10),
                              })
                            }
                            className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Ceiling on audio tokens generated per text chunk.
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                          <span>Early Stop Threshold</span>
                          <span>{Number(formData.fishaudio_early_stop_threshold ?? 1).toFixed(2)}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={Number(formData.fishaudio_early_stop_threshold ?? 1)}
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              fishaudio_early_stop_threshold: parseFloat(event.target.value),
                            })
                          }
                          className="w-full accent-primary"
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Early stopping used during batching. 1.00 keeps the Fish Audio default behavior.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                          <div>
                            <label className="block text-xs font-semibold text-foreground mb-1">
                              Text Normalization
                            </label>
                            <p className="text-[11px] text-muted-foreground">
                              Read numbers, dates, and currency the way a person would. English and Chinese only.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, fishaudio_normalize: !formData.fishaudio_normalize })}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                              formData.fishaudio_normalize ? 'bg-primary' : 'bg-muted'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                formData.fishaudio_normalize ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                          <div>
                            <label className="block text-xs font-semibold text-foreground mb-1">
                              Loudness Normalization
                            </label>
                            <p className="text-[11px] text-muted-foreground">
                              Keep output volume consistent across turns. S2-Pro family only.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setFormData({
                                ...formData,
                                fishaudio_normalize_loudness: !formData.fishaudio_normalize_loudness,
                              })
                            }
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                              formData.fishaudio_normalize_loudness ? 'bg-primary' : 'bg-muted'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                formData.fishaudio_normalize_loudness ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                          <div>
                            <label className="block text-xs font-semibold text-foreground mb-1">
                              Condition On Previous Chunks
                            </label>
                            <p className="text-[11px] text-muted-foreground">
                              Use already-generated audio as context so the voice stays consistent across chunks.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setFormData({
                                ...formData,
                                fishaudio_condition_on_previous_chunks: !formData.fishaudio_condition_on_previous_chunks,
                              })
                            }
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                              formData.fishaudio_condition_on_previous_chunks ? 'bg-primary' : 'bg-muted'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                formData.fishaudio_condition_on_previous_chunks ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.voice_provider === 'fishaudio' && (
                    <div className="p-4 border border-border rounded-lg bg-muted/10 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-1">
                            Enable Expressive Mode
                          </label>
                          <p className="text-xs text-muted-foreground">
                            The agent marks up its own replies with delivery directions and Fish Audio performs
                            them: emotion, emphasis, pauses, and non-verbal sounds. The directions are never
                            spoken and never appear in the transcript.
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={!fishaudioSupportsExpressive}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              fishaudio_expressive_enabled: !formData.fishaudio_expressive_enabled,
                            })
                          }
                          className={`relative ml-4 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                            formData.fishaudio_expressive_enabled && fishaudioSupportsExpressive
                              ? 'bg-primary'
                              : 'bg-muted'
                          } ${!fishaudioSupportsExpressive ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.fishaudio_expressive_enabled && fishaudioSupportsExpressive
                                ? 'translate-x-6'
                                : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {!fishaudioSupportsExpressive && (
                        <p className="text-[11px] text-amber-500">
                          Expressive mode needs an S2-Pro family model. Switch the Fish Audio model to S2.1 Pro,
                          S2.1 Pro Free, or S2 Pro to turn it on.
                        </p>
                      )}

                      {formData.fishaudio_expressive_enabled && fishaudioSupportsExpressive && (
                        <div className="space-y-4 pt-2 border-t border-border">
                          <div>
                            <label className="text-xs font-semibold text-foreground mb-2 block">
                              Speaking Pace
                            </label>
                            <AnimatedDropdown
                              value={formData.fishaudio_expressive_pace || 'normal'}
                              onChange={(value) => setFormData({ ...formData, fishaudio_expressive_pace: value })}
                              options={fishaudioPaceOptions}
                              placeholder="Select pace"
                            />
                            <p className="mt-2 text-[11px] text-muted-foreground">
                              Overall delivery rate the agent aims for across the call.
                            </p>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                            <div>
                              <label className="block text-xs font-semibold text-foreground mb-1">
                                Allow Non-Verbal Sounds
                              </label>
                              <p className="text-[11px] text-muted-foreground">
                                Laughs, sighs, and breath cues where they genuinely fit. Turn off for a strictly
                                composed delivery.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  fishaudio_expressive_nonverbal: !formData.fishaudio_expressive_nonverbal,
                                })
                              }
                              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                                formData.fishaudio_expressive_nonverbal ? 'bg-primary' : 'bg-muted'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  formData.fishaudio_expressive_nonverbal ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                            <div>
                              <label className="block text-xs font-semibold text-foreground mb-1">
                                Allow Filler Words
                              </label>
                              <p className="text-[11px] text-muted-foreground">
                                Occasional "um" or "uh" where a person would pause to think.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  fishaudio_expressive_disfluencies: !formData.fishaudio_expressive_disfluencies,
                                })
                              }
                              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                                formData.fishaudio_expressive_disfluencies ? 'bg-primary' : 'bg-muted'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  formData.fishaudio_expressive_disfluencies ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>

                          <div>
                            <label className="text-xs font-semibold text-foreground mb-2 block">
                              Additional Delivery Rules (Optional)
                            </label>
                            <textarea
                              value={formData.fishaudio_expressive_instructions || ''}
                              onChange={(e) =>
                                setFormData({ ...formData, fishaudio_expressive_instructions: e.target.value })
                              }
                              placeholder="Keep laughter rare, and never sound sarcastic."
                              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              rows={2}
                            />
                            <p className="mt-2 text-[11px] text-muted-foreground">
                              Added to the delivery guide. Use it to narrow the range, not to restate the system
                              prompt.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {voiceControlCount > 0 && (
                    <div className={`grid gap-4 ${voiceControlsGridClass}`}>
                      {supportsTemperatureStability && (
                        <div className="p-4 bg-muted/20 border border-border rounded-lg">
                          <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                            <span>Voice Temperature</span>
                            <span>{Number(formData.voice_temperature ?? 0.7).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={Number(formData.voice_temperature ?? 0.7)}
                            onChange={(event) =>
                              setFormData({
                                ...formData,
                                voice_temperature: parseFloat(event.target.value),
                              })
                            }
                            className="w-full accent-primary"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Higher values add more variation and expressiveness.
                          </p>
                        </div>
                      )}

                      {supportsTemperatureStability && (
                        <div className="p-4 bg-muted/20 border border-border rounded-lg">
                          <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                            <span>Voice Stability</span>
                            <span>{Number(formData.voice_stability ?? 0.6).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={Number(formData.voice_stability ?? 0.6)}
                            onChange={(event) =>
                              setFormData({
                                ...formData,
                                voice_stability: parseFloat(event.target.value),
                              })
                            }
                            className="w-full accent-primary"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Lower values produce more dynamic speech; higher values sound steadier.
                          </p>
                        </div>
                      )}

                      {supportsVoiceVolume && (
                        <div className="p-4 bg-muted/20 border border-border rounded-lg">
                          <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                            <span>Voice Volume</span>
                            <span>{Number(formData.voice_volume ?? 1).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={Number(formData.voice_volume ?? 1)}
                            onChange={(event) =>
                              setFormData({
                                ...formData,
                                voice_volume: parseFloat(event.target.value),
                              })
                            }
                            className="w-full accent-primary"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Adjust overall loudness for calls using this agent.
                          </p>
                        </div>
                      )}

                      {supportsElevenLabsSpeed && (
                        <div className="p-4 bg-muted/20 border border-border rounded-lg">
                          <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                            <span>Speed</span>
                            <span>{Number(formData.elevenlabs_speed ?? 1.0).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.7"
                            max="1.15"
                            step="0.01"
                            value={Number(formData.elevenlabs_speed ?? 1.0)}
                            onChange={(event) =>
                              setFormData({
                                ...formData,
                                elevenlabs_speed: parseFloat(event.target.value),
                              })
                            }
                            className="w-full accent-primary"
                          />
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Adjusts the speed of the voice. Range: 0.7 to 1.15. 1.0 is default speed.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {showNoControlMessage && (
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>
                        This Cartesia voice doesn't support temperature, stability, or volume adjustments. Switch to a Sonic-3 voice or use ElevenLabs for full control.
                      </p>
                    </div>
                  )}

                  {/* Min Silence Duration VAD Setting */}
                  <div className="mt-6 p-4 bg-muted/20 border border-border rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Minimum Silence Duration
                        </label>
                        <p className="text-xs text-muted-foreground">
                          Duration of silence to wait after speech ends to determine if the user has finished speaking
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newValue = !formData.min_silence_duration_enabled;
                          setFormData({ 
                            ...formData, 
                            min_silence_duration_enabled: newValue,
                            // Set default value when enabling if not already set
                            min_silence_duration: newValue && formData.min_silence_duration === undefined ? 0.55 : formData.min_silence_duration
                          });
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          formData.min_silence_duration_enabled ? 'bg-primary' : 'bg-muted'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.min_silence_duration_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {formData.min_silence_duration_enabled && (
                      <div>
                        <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                          <span>Duration (seconds)</span>
                          <span>{Number(formData.min_silence_duration ?? 0.55).toFixed(2)}s</span>
                        </div>
                        <input
                          type="range"
                          min="0.1"
                          max="5.0"
                          step="0.05"
                          value={Number(formData.min_silence_duration ?? 0.55)}
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              min_silence_duration: parseFloat(event.target.value),
                            })
                          }
                          className="w-full accent-primary"
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Lower values make the agent respond faster but may interrupt speech. Higher values wait longer but ensure complete sentences. Default: 0.55s
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Disable Interruption Toggle */}
                  <div className="mt-6 p-4 bg-muted/20 border border-border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Disable Interruption
                        </label>
                        <p className="text-xs text-muted-foreground">
                          When enabled, the user cannot interrupt or barge in while the agent is speaking. The agent will always finish its response before listening to the user.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            disable_interruption: !formData.disable_interruption
                          });
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ml-4 ${
                          formData.disable_interruption ? 'bg-primary' : 'bg-muted'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.disable_interruption ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Who Speaks First */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Who Speaks First
                    </label>
                    <AnimatedDropdown
                      value={formData.who_speaks_first || 'user'}
                      onChange={(value) => setFormData({ ...formData, who_speaks_first: value })}
                      options={[
                        { value: 'user', label: 'User' },
                        { value: 'ai', label: 'AI' }
                      ]}
                      placeholder="Select who speaks first"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {formData.agent_type === 'flow'
                        ? "Choose whether the user or AI speaks first. The flow's start node is the greeting: with AI, the agent says it when the call connects. With User, the agent waits for the caller and says it as its first reply."
                        : 'Choose whether the user or AI speaks first in the conversation'}
                    </p>
                  </div>

                  {/* Custom Greeting Message (shown when AI speaks first). Flow agents greet with their start node. */}
                  {formData.who_speaks_first === 'ai' && formData.agent_type !== 'flow' && (
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Custom Greeting Message
                      </label>
                      <textarea
                        value={formData.greeting_message || ''}
                        onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                        placeholder="Hello! Thank you for calling. How can I assist you today?"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Enter the greeting message the AI will say when it speaks first
                      </p>
                      
                      {/* Non-Interruptible Greeting Toggle */}
                      <div className="mt-4 p-4 bg-muted/20 border border-border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-foreground mb-1">
                              Make Greeting Non-Interruptible
                            </label>
                            <p className="text-xs text-muted-foreground">
                              When enabled, the agent will complete the greeting message regardless of user input. The greeting cannot be interrupted.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ 
                                ...formData, 
                                greeting_non_interruptible: !formData.greeting_non_interruptible
                              });
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              formData.greeting_non_interruptible ? 'bg-primary' : 'bg-muted'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                formData.greeting_non_interruptible ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Outbound Greeting (outbound calls only) */}
                  <div className="mt-6 p-4 bg-muted/20 border border-border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 pr-4">
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Outbound Greeting
                        </label>
                        <p className="text-xs text-muted-foreground">
                          {formData.agent_type === 'flow'
                            ? "When enabled, outbound calls open with the message below instead of the start node's line. The start node's routes still decide what happens next. Inbound calls keep opening with the start node."
                            : 'When enabled, outbound calls open with the message below instead of the greeting above. Inbound calls to this agent keep using the normal greeting.'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            outbound_greeting_enabled: !formData.outbound_greeting_enabled
                          });
                        }}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                          formData.outbound_greeting_enabled ? 'bg-primary' : 'bg-muted'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.outbound_greeting_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {formData.outbound_greeting_enabled && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Outbound Greeting Message
                        </label>
                        <textarea
                          value={formData.outbound_greeting_message || ''}
                          onChange={(e) => setFormData({ ...formData, outbound_greeting_message: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                          placeholder="Hi {{first_name}}, this is Sarah from Acme calling about your appointment."
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          {formData.agent_type === 'flow'
                            ? <>Spoken word for word at the start of every outbound call. Supports the same variables as flow nodes, so {'{{first_name}}'} and any other session variable is filled in per call. Write it so the start node's routes still fit the reply.</>
                            : <>Spoken at the start of every outbound call. Supports the same variables as the greeting above, so {'{{first_name}}'} and any other session variable is filled in per call.</>}
                        </p>
                        {!(formData.outbound_greeting_message || '').trim() && (
                          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                              {formData.agent_type === 'flow'
                                ? "Outbound calls keep opening with the start node until you enter a message here."
                                : 'Outbound calls keep using the normal greeting until you enter a message here.'}
                            </p>
                          </div>
                        )}

                        {/* Non-Interruptible Outbound Greeting Toggle */}
                        <div className="mt-4 p-4 bg-background/40 border border-border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 pr-4">
                              <label className="block text-sm font-medium text-foreground mb-1">
                                Make Outbound Greeting Non-Interruptible
                              </label>
                              <p className="text-xs text-muted-foreground">
                                When enabled, the agent finishes this outbound greeting no matter what the other person says. Off by default, and it applies to outbound calls only. Inbound calls keep following the greeting setting above.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  outbound_greeting_non_interruptible: !formData.outbound_greeting_non_interruptible
                                });
                              }}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                                formData.outbound_greeting_non_interruptible ? 'bg-primary' : 'bg-muted'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  formData.outbound_greeting_non_interruptible ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}


                    {/* Outbound Who Speaks First */}
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 pr-4">
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Outbound Who Speaks First
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Off by default, so the agent opens every outbound call itself. Turn it on to choose who speaks first on outbound calls. Inbound calls always follow the Who Speaks First setting above.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              outbound_who_speaks_first_enabled: !formData.outbound_who_speaks_first_enabled
                            });
                          }}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                            formData.outbound_who_speaks_first_enabled ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.outbound_who_speaks_first_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {formData.outbound_who_speaks_first_enabled && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Who Speaks First on Outbound Calls
                          </label>
                          <AnimatedDropdown
                            value={formData.outbound_who_speaks_first || 'ai'}
                            onChange={(value) => setFormData({ ...formData, outbound_who_speaks_first: value })}
                            options={[
                              { value: 'ai', label: 'AI' },
                              { value: 'user', label: 'User' }
                            ]}
                            placeholder="Select who speaks first on outbound calls"
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            AI opens the call with the greeting as soon as the line connects. User holds the greeting back: the agent stays silent until the person who answered speaks, then delivers the greeting as its first reply.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* PII Tab */}
              {activeTab === 'pii' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      PII Redaction Policy
                    </label>
                    <AnimatedDropdown
                      value={formData.pii_policy}
                      onChange={(value) => setFormData({ ...formData, pii_policy: value })}
                      options={[
                        { value: 'off', label: 'Off - No PII redaction' },
                        { value: 'mask', label: 'Mask - Replace PII with placeholders' },
                        { value: 'drop', label: 'Drop - Remove PII completely' }
                      ]}
                      placeholder="Select PII policy"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {formData.pii_policy === 'off' && 'Analysis stored as-is with all PII'}
                      {formData.pii_policy === 'mask' && 'PII replaced with [NAME], [EMAIL], etc.'}
                      {formData.pii_policy === 'drop' && 'PII completely removed from stored analysis'}
                    </p>
                  </div>

                  {/* Default PII Types */}
                  {formData.pii_policy !== 'off' && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-foreground">
                          Active PII Types (Will Be Redacted)
                        </label>
                        <span className="text-xs text-muted-foreground">
                          {13 - disabledDefaultTypes.length} active, {disabledDefaultTypes.length} removed
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 p-4 bg-muted/20 rounded-lg border border-border">
                        {['name', 'email', 'phone', 'ssn', 'credit_card', 'address', 'date_of_birth', 'driver_license', 'passport', 'bank_account', 'ip_address', 'medical_record', 'insurance_id']
                          .filter(type => !disabledDefaultTypes.includes(type))
                          .map((type) => {
                            const placeholder = type === 'name' ? '[NAME]' :
                                              type === 'email' ? '[EMAIL]' :
                                              type === 'phone' ? '[PHONE]' :
                                              type === 'ssn' ? '[SSN]' :
                                              type === 'credit_card' ? '[CREDIT_CARD]' :
                                              type === 'address' ? '[ADDRESS]' :
                                              type === 'date_of_birth' ? '[DATE_OF_BIRTH]' :
                                              type === 'driver_license' ? '[DRIVER_LICENSE]' :
                                              type === 'passport' ? '[PASSPORT]' :
                                              type === 'bank_account' ? '[BANK_ACCOUNT]' :
                                              type === 'ip_address' ? '[IP_ADDRESS]' :
                                              type === 'medical_record' ? '[MEDICAL_RECORD]' :
                                              type === 'insurance_id' ? '[INSURANCE_ID]' : `[${type.toUpperCase()}]`;
                            return (
                              <div key={type} className="flex items-center justify-between p-2 bg-background border border-border rounded">
                                <div className="flex items-center gap-2 text-sm">
                                  <Shield className="w-4 h-4 text-primary" />
                                  <span className="text-foreground font-mono">{type}</span>
                                  <span className="text-muted-foreground">→</span>
                                  <span className="text-primary font-mono">{placeholder}</span>
                                </div>
                                <button
                                  onClick={() => {
                                    // Completely remove this type - add to disabled list
                                    const newDisabled = [...disabledDefaultTypes, type];
                                    setDisabledDefaultTypes(newDisabled);
                                    // Update formData with disabled defaults
                                    const piiData = { ...customPiiTypes, _disabled_defaults: newDisabled };
                                    const piiDataStr = JSON.stringify(piiData);
                                    const activeTypes = ['name', 'email', 'phone', 'ssn', 'credit_card', 'address', 'date_of_birth', 'driver_license', 'passport', 'bank_account', 'ip_address', 'medical_record', 'insurance_id'].filter(t => !newDisabled.includes(t));
                                    setFormData({ ...formData, custom_pii_types: piiDataStr });
                                    setToast({ message: `"${type}" will NOT be redacted anymore`, type: 'success' });
                                  }}
                                  className="p-1.5 text-destructive hover:bg-destructive/10 rounded"
                                  title="Remove this PII type - it will NOT be redacted"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                      </div>
                      {disabledDefaultTypes.length > 0 && (
                        <div className="mt-3 p-3 bg-muted/30 rounded-lg border border-border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Removed Types (Will NOT Be Redacted):
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {disabledDefaultTypes.map((type) => {
                              const placeholder = type === 'name' ? '[NAME]' :
                                                type === 'email' ? '[EMAIL]' :
                                                type === 'phone' ? '[PHONE]' :
                                                type === 'ssn' ? '[SSN]' :
                                                type === 'credit_card' ? '[CREDIT_CARD]' :
                                                type === 'address' ? '[ADDRESS]' :
                                                type === 'date_of_birth' ? '[DATE_OF_BIRTH]' :
                                                type === 'driver_license' ? '[DRIVER_LICENSE]' :
                                                type === 'passport' ? '[PASSPORT]' :
                                                type === 'bank_account' ? '[BANK_ACCOUNT]' :
                                                type === 'ip_address' ? '[IP_ADDRESS]' :
                                                type === 'medical_record' ? '[MEDICAL_RECORD]' :
                                                type === 'insurance_id' ? '[INSURANCE_ID]' : `[${type.toUpperCase()}]`;
                              return (
                                <div key={type} className="flex items-center gap-2 px-2 py-1 bg-muted rounded text-sm">
                                  <span className="text-muted-foreground font-mono line-through">{type}</span>
                                  <span className="text-muted-foreground">→</span>
                                  <span className="text-muted-foreground font-mono line-through">{placeholder}</span>
                                  <button
                                    onClick={() => {
                                      const newDisabled = disabledDefaultTypes.filter(t => t !== type);
                                      setDisabledDefaultTypes(newDisabled);
                                      // Update formData with disabled defaults
                                      const piiData = { ...customPiiTypes, _disabled_defaults: newDisabled };
                                      const piiDataStr = JSON.stringify(piiData);
                                      const activeTypes = ['name', 'email', 'phone', 'ssn', 'credit_card', 'address', 'date_of_birth', 'driver_license', 'passport', 'bank_account', 'ip_address', 'medical_record', 'insurance_id'].filter(t => !newDisabled.includes(t));
                                      setFormData({ ...formData, custom_pii_types: piiDataStr });
                                      setToast({ message: `"${type}" will now be redacted again`, type: 'success' });
                                    }}
                                    className="ml-1 text-primary hover:text-primary/80"
                                    title="Re-enable this PII type"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Custom PII Types */}
                  {formData.pii_policy !== 'off' && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-foreground">
                          Custom PII Types
                        </label>
                        <button
                          onClick={() => {
                            const newTypes = { ...customPiiTypes, [newPiiType.name]: newPiiType.placeholder || `[${newPiiType.name.toUpperCase()}]` };
                            setCustomPiiTypes(newTypes);
                            // Preserve disabled defaults when updating
                            const piiData = { ...newTypes, _disabled_defaults: disabledDefaultTypes };
                            setFormData({ ...formData, custom_pii_types: JSON.stringify(piiData) });
                            setNewPiiType({ name: '', placeholder: '' });
                          }}
                          disabled={!newPiiType.name}
                          className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-4 h-4" />
                          Add Type
                        </button>
                      </div>
                      
                      {/* Add new PII type form */}
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={newPiiType.name}
                          onChange={(e) => setNewPiiType({ ...newPiiType, name: e.target.value })}
                          placeholder="Type name (e.g., customer_id)"
                          className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        />
                        <input
                          type="text"
                          value={newPiiType.placeholder}
                          onChange={(e) => setNewPiiType({ ...newPiiType, placeholder: e.target.value })}
                          placeholder="Placeholder (e.g., [CUSTOMER_ID])"
                          className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                        />
                      </div>

                      {/* List custom PII types */}
                      {Object.keys(customPiiTypes).length > 0 && (
                        <div className="space-y-2">
                          {Object.entries(customPiiTypes).map(([name, placeholder]) => (
                            <div key={name} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                              <div className="flex items-center gap-3">
                                <Shield className="w-4 h-4 text-primary" />
                                <span className="text-foreground font-mono text-sm">{name}</span>
                                <span className="text-muted-foreground">→</span>
                                <span className="text-primary font-mono text-sm">{placeholder}</span>
                              </div>
                              <button
                                onClick={() => {
                                  const newTypes = { ...customPiiTypes };
                                  delete newTypes[name];
                                  setCustomPiiTypes(newTypes);
                                  // Preserve disabled defaults when updating
                                  const piiData = { ...newTypes, _disabled_defaults: disabledDefaultTypes };
                                  setFormData({ ...formData, custom_pii_types: JSON.stringify(piiData) });
                                  setToast({ message: `Removed custom PII type "${name}"`, type: 'success' });
                                }}
                                className="p-1.5 text-destructive hover:bg-destructive/10 rounded"
                                title="Remove this custom PII type"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {Object.keys(customPiiTypes).length === 0 && (
                        <p className="text-xs text-muted-foreground italic">No custom PII types added</p>
                      )}
                    </div>
                  )}

                  {/* PII Preview */}
                  {formData.pii_policy !== 'off' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Preview Redaction
                      </label>
                      <textarea
                        value={piiPreviewText}
                        onChange={(e) => setPiiPreviewText(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm resize-none mb-2"
                        placeholder="Enter text with PII to preview redaction (e.g., 'My name is John and my email is john@example.com')"
                      />
                      <button
                        onClick={async () => {
                          if (!piiPreviewText.trim()) return;
                          setPiiPreviewLoading(true);
                          try {
                            // Build the final list of active types (defaults minus disabled, plus custom)
                            const defaultTypes = {
                              'name': '[NAME]',
                              'email': '[EMAIL]',
                              'phone': '[PHONE]',
                              'ssn': '[SSN]',
                              'credit_card': '[CREDIT_CARD]',
                              'address': '[ADDRESS]',
                              'date_of_birth': '[DATE_OF_BIRTH]',
                              'driver_license': '[DRIVER_LICENSE]',
                              'passport': '[PASSPORT]',
                              'bank_account': '[BANK_ACCOUNT]',
                              'ip_address': '[IP_ADDRESS]',
                              'medical_record': '[MEDICAL_RECORD]',
                              'insurance_id': '[INSURANCE_ID]'
                            };
                            
                            // Remove disabled defaults
                            const activeDefaults = {};
                            for (const [type, placeholder] of Object.entries(defaultTypes)) {
                              if (!disabledDefaultTypes.includes(type)) {
                                activeDefaults[type] = placeholder;
                              }
                            }
                            
                            // Merge with custom types - this is the FINAL list to send
                            const finalActiveTypes = { ...activeDefaults, ...customPiiTypes };
                            
                            
                            const response = await api.post('/pii/preview', {
                              text: piiPreviewText,
                              policy: formData.pii_policy,
                              custom_pii_types: Object.keys(finalActiveTypes).length > 0 ? JSON.stringify(finalActiveTypes) : null
                            });
                            setPiiPreviewResult(response.data);
                          } catch (error) {
                            setToast({ message: 'Preview failed: ' + (error.response?.data?.detail || error.message), type: 'error' });
                          } finally {
                            setPiiPreviewLoading(false);
                          }
                        }}
                        disabled={!piiPreviewText.trim() || piiPreviewLoading}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {piiPreviewLoading ? 'Processing...' : 'Preview Redaction'}
                      </button>
                      
                      {piiPreviewResult && (
                        <div className="mt-4 space-y-3">
                          <div className="p-3 bg-muted/20 rounded-lg border border-border">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Original:</p>
                            <p className="text-sm text-foreground">{piiPreviewResult.original_text}</p>
                          </div>
                          <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                            <p className="text-xs font-medium text-primary mb-1">Redacted:</p>
                            <p className="text-sm text-foreground">{piiPreviewResult.redacted_text}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Send Redacted Data to Webhook Switch */}
                  {formData.pii_policy !== 'off' && (
                    <div className="flex items-center gap-3 p-4 bg-muted/20 rounded-lg border border-border">
                      <AnimatedCheckbox
                        id="send_redacted_to_webhook"
                        checked={formData.send_redacted_to_webhook || false}
                        onChange={(e) => setFormData({ ...formData, send_redacted_to_webhook: e.target.checked })}
                        label="Send Redacted Data to Webhook"
                        labelClassName="text-sm font-medium"
                        size="md"
                      />
                      <p className="text-xs text-muted-foreground ml-auto">
                        When enabled, webhooks will receive redacted summary and transcript instead of original data
                      </p>
                    </div>
                  )}

                  {/* Disable Call Recording Switch */}
                  <div className="flex items-center gap-3 p-4 bg-muted/20 rounded-lg border border-border">
                    <AnimatedCheckbox
                      id="disable_call_recording"
                      checked={formData.disable_call_recording || false}
                      onChange={(e) => setFormData({ ...formData, disable_call_recording: e.target.checked })}
                      label="Disable Call Recording"
                      labelClassName="text-sm font-medium"
                      size="md"
                    />
                    <p className="text-xs text-muted-foreground ml-auto">
                      When enabled, call recording will be skipped for inbound, outbound, and test calls for this agent
                    </p>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-muted/20 rounded-lg border border-border">
                    <AnimatedCheckbox
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      label="Active"
                      labelClassName="text-sm font-medium"
                      size="md"
                    />
                    <p className="text-xs text-muted-foreground ml-auto">
                      Active agents are available for use
                    </p>
                  </div>
                </div>
              )}

              {/* Webhooks Tab */}
              {activeTab === 'webhooks' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-primary" />
                      Agent-Specific Webhooks
                    </h4>
                    <p className="text-sm text-muted-foreground mb-6">
                      Configure webhook URLs specific to this agent. If set, these will override organization-level webhooks for this agent only.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Inbound Webhook URL
                        </label>
                        <input
                          type="url"
                          value={formData.inbound_webhook_url || ''}
                          onChange={(e) => setFormData({ ...formData, inbound_webhook_url: e.target.value })}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="https://integration.braincx.ai/webhook/braincx/inbound"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          URL to receive pre-call personalization data. Leave empty to use organization-level webhook.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Outbound Webhook URL
                        </label>
                        <input
                          type="url"
                          value={formData.outbound_webhook_url || ''}
                          onChange={(e) => setFormData({ ...formData, outbound_webhook_url: e.target.value })}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="https://integration.braincx.ai/webhook/braincx/outbound"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          URL to receive call events (call_started, call_ended, call_analyzed). Leave empty to use organization-level webhook.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Chat Outbound Webhook URL
                        </label>
                        <input
                          type="url"
                          value={formData.chat_outbound_webhook_url || ''}
                          onChange={(e) => setFormData({ ...formData, chat_outbound_webhook_url: e.target.value })}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="https://integration.braincx.ai/webhook/braincx/chat-outbound"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          URL to receive chat events (chat_started, chat_ended, chat_analyzed) for this agent's chat widget, chat embed, and combo widget chat channel. Leave empty to use organization-level webhook.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'transfer' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                      <PhoneCall className="w-5 h-5 text-primary" />
                      Call Transfer Configuration
                    </h4>
                    <p className="text-sm text-muted-foreground mb-6">
                      Configure call transfer settings for this agent. When enabled, the agent can transfer calls to a specified number using either warm or cold transfer.
                    </p>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Enable Call Transfer
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Allow this agent to transfer calls to another number
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, transfer_enabled: !formData.transfer_enabled })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.transfer_enabled ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.transfer_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {formData.transfer_enabled && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Transfer Type
                            </label>
                            <select
                              value={formData.transfer_type || 'warm'}
                              onChange={(e) => setFormData({ ...formData, transfer_type: e.target.value })}
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="warm">Warm Transfer</option>
                              <option value="cold">Cold Transfer</option>
                            </select>
                            <p className="text-xs text-muted-foreground mt-2">
                              <strong>Warm Transfer:</strong> Agent places caller on hold, calls supervisor in consultation room, provides summary, then connects supervisor to caller.
                              <br />
                              <strong>Cold Transfer:</strong> Direct transfer without consultation - caller is immediately transferred to the target number.
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Transfer Target Number
                            </label>
                            <input
                              type="tel"
                              value={formData.transfer_target_number || ''}
                              onChange={(e) => setFormData({ ...formData, transfer_target_number: e.target.value })}
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              placeholder="+1234567890"
                              pattern="^\+[1-9]\d{1,14}$"
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              Phone number in E.164 format (e.g., +1234567890). This is the number calls will be transferred to when no conditional rule below matches.
                            </p>
                          </div>

                          <TransferRulesEditor formData={formData} setFormData={setFormData} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'agent-transfer' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Bot className="w-5 h-5 text-primary" />
                      Agent-to-Agent Transfer (Test Sessions)
                    </h4>
                    <p className="text-sm text-muted-foreground mb-6">
                      Configure agent-to-agent transfer for web test sessions. When enabled, users can request to be transferred to another agent during a test session. This feature only works for test sessions, not production phone calls.
                    </p>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Enable Agent Transfer
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Allow this agent to transfer test sessions to other agents based on user demand
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, agent_transfer_enabled: !formData.agent_transfer_enabled })}
                          className={`
                            relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                            ${formData.agent_transfer_enabled ? 'bg-primary' : 'bg-muted'}
                          `}
                        >
                          <span
                            className={`
                              inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300
                              ${formData.agent_transfer_enabled ? 'translate-x-6' : 'translate-x-1'}
                            `}
                          />
                        </button>
                      </div>

                      {formData.agent_transfer_enabled && (
                        <div className="space-y-4 p-4 bg-background border border-border rounded-lg">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Available Agents for Transfer
                            </label>
                            <p className="text-xs text-muted-foreground mb-4">
                              Select which agents users can be transferred to. Users can request to speak with any of these agents by name during a test session.
                            </p>
                            {(() => {
                              // Parse selected agent IDs from JSON string
                              let selectedAgentIds = [];
                              try {
                                if (formData.agent_transfer_agents) {
                                  selectedAgentIds = JSON.parse(formData.agent_transfer_agents);
                                  if (!Array.isArray(selectedAgentIds)) {
                                    selectedAgentIds = [];
                                  }
                                }
                              } catch (e) {
                                selectedAgentIds = [];
                              }

                              const handleAgentToggle = (agentId) => {
                                const currentIds = [...selectedAgentIds];
                                const index = currentIds.indexOf(agentId);
                                
                                if (index > -1) {
                                  currentIds.splice(index, 1);
                                } else {
                                  currentIds.push(agentId);
                                }
                                
                                setFormData({
                                  ...formData,
                                  agent_transfer_agents: JSON.stringify(currentIds)
                                });
                              };

                              return (
                                <div className="space-y-3">
                                  {loadingAgents ? (
                                    <div className="flex items-center justify-center p-8">
                                      <LoadingSpinner />
                                    </div>
                                  ) : availableAgents.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center p-4">
                                      No other agents available for transfer.
                                    </p>
                                  ) : (
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                      {availableAgents.map((agent) => (
                                        <label
                                          key={agent.id}
                                          className="flex items-center p-3 bg-muted/50 hover:bg-muted border border-border rounded-lg cursor-pointer transition-colors"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={selectedAgentIds.includes(agent.id)}
                                            onChange={() => handleAgentToggle(agent.id)}
                                            className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                                          />
                                          <span className="ml-3 text-sm font-medium text-foreground">
                                            {agent.name}
                                          </span>
                                          {agent.id === editingAgent?.id && (
                                            <span className="ml-2 text-xs text-muted-foreground">(Current)</span>
                                          )}
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                  {selectedAgentIds.length > 0 && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      {selectedAgentIds.length} agent{selectedAgentIds.length !== 1 ? 's' : ''} selected
                                    </p>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'backchanneling' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-primary" />
                      Backchanneling Affirmations
                    </h4>
                    <p className="text-sm text-muted-foreground mb-6">
                      Enable backchanneling affirmations to make conversations more natural. When enabled, the agent will use short affirmations (like "uh-huh", "mhmm") when the user pauses mid-sentence, showing active listening and engagement.
                    </p>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Enable Backchanneling
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Allow the agent to use backchanneling affirmations during conversations
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, backchanneling_enabled: !formData.backchanneling_enabled })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.backchanneling_enabled ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.backchanneling_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {formData.backchanneling_enabled && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Custom Backchanneling Words
                          </label>
                          <textarea
                            value={backchannelingWordsInput}
                            onChange={(e) => {
                              setBackchannelingWordsInput(e.target.value);
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value === '') {
                                // Explicitly set to null to clear
                                setFormData({ ...formData, backchanneling_words: null });
                                setBackchannelingWordsInput('');
                              } else {
                                // Split by comma and clean up
                                const words = value.split(',').map(w => w.trim()).filter(w => w.length > 0);
                                if (words.length > 0) {
                                  setFormData({ ...formData, backchanneling_words: JSON.stringify(words) });
                                } else {
                                  setFormData({ ...formData, backchanneling_words: null });
                                  setBackchannelingWordsInput('');
                                }
                              }
                            }}
                            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="uh-huh, mhmm, I see, right, okay"
                            rows={3}
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            Enter custom backchanneling words or phrases separated by commas. These will be combined with default affirmations. Examples: "uh-huh", "mhmm", "I see", "right", "okay", "got it"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'data-extraction' && (
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <div className="flex-shrink-0 mb-6">
                    <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Database className="w-5 h-5 text-primary" />
                      Post-Call Data Extraction
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Configure data fields to extract from conversation transcripts using LLM. Extracted data will be included in the call_analyzed webhook and session analysis.
                    </p>
                  </div>

                  {/* Two Column Layout */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0 overflow-hidden">
                      {/* Left Column - LLM Selector + Fields List */}
                      <div className="flex flex-col h-full min-h-0 overflow-hidden">
                        <div className="flex-shrink-0 mb-4">
                          {/* LLM Model Selector - Simple Field */}
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Extraction LLM Model
                          </label>
                          <select
                            value={formData.extraction_llm_model || 'gpt-4.1'}
                            onChange={(e) => setFormData({ ...formData, extraction_llm_model: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          >
                            <option value="gpt-4.1">GPT-4.1 (Recommended - Best Accuracy)</option>
                            <option value="gpt-4.1-mini">GPT-4.1 Mini (Fast & Accurate)</option>
                            <option value="gpt-4o">GPT-4o (High Accuracy)</option>
                            <option value="gpt-4o-mini">GPT-4o Mini (Fast & Cost-Effective)</option>
                            <option value="gpt-4-turbo">GPT-4 Turbo (Balanced)</option>
                            <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Budget)</option>
                          </select>
                        </div>

                        {/* Extraction Fields List - Scrollable */}
                        <div className="flex-1 flex flex-col min-h-0 bg-muted/20 rounded-lg border border-border/50 p-5 overflow-hidden">
                          <div className="flex items-center justify-between mb-4 flex-shrink-0">
                            <h5 className="text-md font-semibold text-foreground flex items-center gap-2">
                              <Database className="w-4 h-4 text-primary" />
                              Extraction Fields
                            </h5>
                            <button
                              type="button"
                              onClick={() => {
                                setShowExtractionFieldForm(true);
                                setEditingExtractionField(null);
                                setNewExtractionField({
                                  name: '',
                                  description: '',
                                  data_type: 'text',
                                  skip_if_not_found: false,
                                  format_example: '',
                                  choices: '',
                                  display_order: extractionFields.length,
                                  is_active: true
                                });
                              }}
                              className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 flex items-center gap-2 text-sm font-medium"
                            >
                              <Plus className="w-4 h-4" />
                              Add Field
                            </button>
                          </div>

                          <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2 min-h-0" style={{ scrollBehavior: 'smooth' }}>
                            {loadingExtractionFields ? (
                              <div className="flex items-center justify-center py-8">
                                <LoadingSpinner size="md" text="Loading..." />
                              </div>
                            ) : extractionFields.length === 0 ? (
                              <div className="text-center py-8 text-muted-foreground">
                                <Database className="w-10 h-10 mx-auto mb-3 opacity-50" />
                                <p className="text-sm font-medium mb-1">No extraction fields</p>
                                <p className="text-xs">Add fields to extract structured data</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {extractionFields.map((field) => (
                                  <div key={field.id} className="p-3 bg-background border border-border rounded-lg hover:border-primary/30 transition-colors">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                          <span className="font-medium text-foreground text-sm">{field.name}</span>
                                          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded font-medium">
                                            {field.data_type}
                                          </span>
                                          {!field.is_active && (
                                            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded">
                                              Inactive
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mb-1.5 line-clamp-2">{field.description}</p>
                                        {field.format_example && (
                                          <p className="text-xs text-muted-foreground mb-1">
                                            <span className="font-medium">Example:</span> {field.format_example}
                                          </p>
                                        )}
                                        {field.skip_if_not_found && (
                                          <p className="text-xs text-warning flex items-center gap-1 mt-1">
                                            <AlertCircle className="w-3 h-3" />
                                            Skips if not found
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                          type="button"
                                          onClick={() => handleEditExtractionField(field)}
                                          className="p-1.5 hover:bg-muted rounded transition-colors"
                                          title="Edit"
                                        >
                                          <Edit3 className="w-3.5 h-3.5 text-muted-foreground" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteExtractionField(field.id, field.name)}
                                          className="p-1.5 hover:bg-destructive/10 rounded transition-colors"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right Column - Add/Edit Form - Scrollable */}
                      <div className="flex flex-col h-full min-h-0 overflow-hidden">
                        {showExtractionFieldForm ? (
                          <div className="flex-1 flex flex-col min-h-0 bg-muted/20 rounded-lg border border-border/50 p-5 overflow-hidden">
                            <h5 className="text-md font-semibold text-foreground mb-4 flex-shrink-0">
                              {editingExtractionField ? 'Edit Extraction Field' : 'Create Extraction Field'}
                            </h5>
                            <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2 min-h-0" style={{ scrollBehavior: 'smooth' }}>
                              <div className="space-y-4">
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-2">
                                    Field Name *
                                  </label>
                                  <input
                                    type="text"
                                    value={newExtractionField.name}
                                    onChange={(e) => setNewExtractionField({ ...newExtractionField, name: e.target.value })}
                                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                    placeholder="customer_name"
                                    required
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Unique identifier (e.g., customer_name, appointment_date)
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-2">
                                    Description *
                                  </label>
                                  <textarea
                                    value={newExtractionField.description}
                                    onChange={(e) => setNewExtractionField({ ...newExtractionField, description: e.target.value })}
                                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                    rows={3}
                                    placeholder="Extract the customer's full name from the conversation"
                                    required
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Describe what data to extract
                                  </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                      Data Type *
                                    </label>
                                    <AnimatedDropdown
                                      value={newExtractionField.data_type}
                                      onChange={(value) => {
                                        // Reset choices when switching to/from selector type
                                        const updatedField = { ...newExtractionField, data_type: value };
                                        if (value !== 'selector') {
                                          updatedField.choices = [];
                                        } else {
                                          // Initialize with one empty choice if switching to selector and choices is empty
                                          if (!Array.isArray(updatedField.choices) || updatedField.choices.length === 0) {
                                            updatedField.choices = [''];
                                          }
                                        }
                                        // Array type doesn't use choices, so clear them
                                        if (value === 'array') {
                                          updatedField.choices = [];
                                        }
                                        setNewExtractionField(updatedField);
                                      }}
                                      options={[
                                        { value: 'text', label: 'Text' },
                                        { value: 'number', label: 'Number' },
                                        { value: 'boolean', label: 'Boolean' },
                                        { value: 'selector', label: 'Selector' },
                                        { value: 'array', label: 'Array' }
                                      ]}
                                      placeholder="Select data type"
                                    />
                                  </div>

                                  <div className="flex items-end">
                                    <div className="flex items-center gap-2 w-full">
                                      <input
                                        type="checkbox"
                                        id="skip_if_not_found"
                                        checked={newExtractionField.skip_if_not_found}
                                        onChange={(e) => setNewExtractionField({ ...newExtractionField, skip_if_not_found: e.target.checked })}
                                        className="w-4 h-4"
                                      />
                                      <label htmlFor="skip_if_not_found" className="text-sm text-foreground cursor-pointer">
                                        Skip if not found
                                      </label>
                                    </div>
                                  </div>
                                </div>

                                {newExtractionField.data_type === 'selector' ? (
                                  <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                      Choices (Required for Selector) *
                                    </label>
                                    <div className="space-y-2">
                                      {newExtractionField.choices.map((choice, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                          <input
                                            type="text"
                                            value={choice}
                                            onChange={(e) => handleChoiceChange(index, e.target.value)}
                                            className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                            placeholder={`Choice ${index + 1}`}
                                          />
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveChoice(index)}
                                            className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                            title="Remove choice"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        onClick={handleAddChoice}
                                        className="w-full px-4 py-2 border-2 border-dashed border-border hover:border-primary/50 rounded-lg text-foreground/70 hover:text-foreground transition-colors flex items-center justify-center gap-2"
                                      >
                                        <Plus className="w-4 h-4" />
                                        Add Choice
                                      </button>
                                    </div>
                                    {newExtractionField.choices.length === 0 && (
                                      <p className="text-xs text-muted-foreground mt-2">
                                        Click "Add Choice" to add options for this selector field
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                      Format Example (Optional)
                                    </label>
                                    <input
                                      type="text"
                                      value={newExtractionField.format_example}
                                      onChange={(e) => setNewExtractionField({ ...newExtractionField, format_example: e.target.value })}
                                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                      placeholder="YYYY-MM-DD or John Doe"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Example format (e.g., "2025-01-15" for dates)
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 pt-4 mt-4 border-t border-border flex-shrink-0">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSaveExtractionField(e);
                                }}
                                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all text-sm font-medium"
                              >
                                {editingExtractionField ? 'Update Field' : 'Create Field'}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowExtractionFieldForm(false);
                                  setEditingExtractionField(null);
                                }}
                                className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex-1 bg-muted/20 rounded-lg border border-border/50 p-5 flex items-center justify-center min-h-0">
                            <div className="text-center text-muted-foreground">
                              <Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
                              <p className="text-sm">Select a field to edit or click "Add Field" to create a new one</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
               <button
                 type="button"
                 onClick={onCancel}
                 className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
               >
                 Cancel
               </button>
               <button
                 type="submit"
                 className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30"
               >
                 {editingAgent ? 'Save Agent' : 'Create Agent'}
               </button>
            </div>
          </form>
        )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showAvatarPanelCustomizer && editingAgent && (
        <AvatarPanelThemeCustomizer
          agent={editingAgent}
          onClose={() => setShowAvatarPanelCustomizer(false)}
          onSave={() => setShowAvatarPanelCustomizer(false)}
        />
      )}

      {/* Delete Function Confirmation Modal */}
      {deleteFunctionModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 w-full max-w-md mx-4 shadow-elegant">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                Delete Function?
              </h3>
            </div>
            
            <p className="text-muted-foreground text-sm mb-2">
              Are you sure you want to delete <span className="text-foreground font-medium">"{deleteFunctionModal.functionName}"</span>?
            </p>
            <p className="text-muted-foreground text-xs mb-6">
              This action cannot be undone. The function will be permanently removed.
            </p>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteFunctionModal({ open: false, functionId: null, functionName: '' })}
                className="px-4 py-2 bg-muted/50 hover:bg-muted border border-border/60 text-foreground rounded-lg transition-all duration-300 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteFunction}
                className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg transition-all duration-300 text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Extraction Field Confirmation Modal */}
      {extractionFieldDeleteModal.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-destructive/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Delete Extraction Field</h3>
            </div>
            <p className="text-muted-foreground mb-2">
              Are you sure you want to delete <span className="text-foreground font-medium">"{extractionFieldDeleteModal.fieldName}"</span>?
            </p>
            <p className="text-muted-foreground text-xs mb-6">
              This action cannot be undone. The extraction field will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setExtractionFieldDeleteModal({ show: false, fieldId: null, fieldName: '' })}
                className="flex-1 px-4 py-2 bg-background/30 border border-border/60 rounded-lg text-foreground hover:bg-background/50 transition-all duration-300 hover:scale-105 hover:shadow-md hover:shadow-primary/10"
              >
                Cancel
              </button>
              <button
                onClick={confirmExtractionFieldDelete}
                className="flex-1 px-4 py-2 bg-destructive text-primary-foreground rounded-lg hover:bg-destructive/90 hover:shadow-lg hover:shadow-destructive/20 hover:scale-105 transition-all duration-300"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentFormModal;
